import * as React from "react";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  Icon,
  IntentBadge,
  PageHeader,
  SelectableRow,
  StatCard,
  StatusBadge,
  type DataTableColumn,
  type Intent,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import { evidence } from "@excellent/ui/api/contracts/evidence";
import type { UniversalConsoleOverview } from "@excellent/core/verification-universal-surfaces-client";
import { resolveStoredVerdict } from "@excellent/core/verification-verdict-law";
import { VerificationQueueFocus } from "./verification-queue-focus";
import type {
  EvidenceReadinessDto,
  EvidenceSoc2AuditPreparationExportPacketDto,
  EvidenceSoc2CsvParseIssueDto,
  EvidenceSoc2EvidenceInventoryDto,
  EvidenceSoc2FindingDto,
  EvidenceSoc2NotificationOutboxDto,
  EvidenceSoc2PreflightPolicyStudioStateDto,
  EvidenceSoc2Phase1ScorecardDto,
  EvidenceSoc2PreflightBatchJobDto,
  EvidenceSoc2PreflightImportOnboardingStateDto,
  EvidenceSoc2PreflightPackageDto,
  EvidenceSoc2PreflightReportDto,
  EvidenceSoc2PreflightRunSummaryDto,
  EvidenceSoc2PreflightStoredRunDto,
  EvidenceSoc2RemediationDto,
  EvidenceSoc2ReviewDecisionKindDto,
  EvidenceSoc2ReviewQueueDto,
  EvidenceSoc2ReviewQueueItemDto,
  EvidenceSoc2ScheduleReportDto,
  EvidenceSoc2SemanticWorkerReviewDto,
  EvidenceSoc2SourceAlertReportDto,
  EvidenceSoc2PreflightStatusDto,
  EvidenceSoc2TemplateIdDto,
  EvidenceTaskSummaryDto,
  EvidenceWorkflowCandidateDto,
  EvidenceWorkflowCandidateStatusDto,
  EvidenceWorkflowTemplateDraftResultDto,
  SilentApproveDto,
} from "@excellent/ui/api/contracts/evidence";

const MUTED = "type-subtext text-muted-foreground";
const NUM = "tabular-nums";

type ReadinessTile = {
  label: string;
  value: string;
  intent: Intent;
  detail: string;
};

type D1LoopStage = {
  key: string;
  label: string;
  value: string;
  intent: Intent;
  ready: boolean;
  detail: string;
  href?: string;
  action?: string;
};

type D1LoopBlocker = {
  label: string;
  detail: string;
  href: string;
  action: string;
};

type WorkflowTemplateSummary = {
  id: string;
  title: string;
  style: string;
  objective: string;
  requirement: string;
  expectedPredicates: string[];
};

function formatMs(ms: number | null | undefined): string {
  if (ms == null) return "not measured";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
}

type VerifierHomeProps = {
  tasks: EvidenceTaskSummaryDto[];
  agentImprovementEvidence?: React.ReactNode;
  silent: SilentApproveDto | undefined;
  templates?: WorkflowTemplateSummary[];
  readiness: EvidenceReadinessDto | undefined;
  universalOverview?: UniversalConsoleOverview;
  soc2Preflight: EvidenceSoc2PreflightReportDto | undefined;
  soc2ImportOnboardingState: EvidenceSoc2PreflightImportOnboardingStateDto | undefined;
  soc2PolicyStudioState: EvidenceSoc2PreflightPolicyStudioStateDto | undefined;
  soc2BatchJobs: EvidenceSoc2PreflightBatchJobDto[];
  soc2Runs: EvidenceSoc2PreflightRunSummaryDto[];
  soc2PreflightOpen: boolean;
  workflowCandidates: EvidenceWorkflowCandidateDto[];
  listError: unknown;
  listLoading: boolean;
  onOpen: (id: number) => void;
  onOpenSoc2Preflight: () => void;
  onSoc2RunImported: () => void;
  onWorkflowCandidateSaved: () => void;
};

const CANDIDATE_FIELD =
  "min-h-9 rounded-md border border-border bg-background px-3 py-2 type-body outline-none transition-colors focus:border-primary";

function consequenceIntent(
  tag: string | null | undefined,
): "neutral" | "warning" | "danger" {
  if (!tag || tag === "none") return "neutral";
  if (tag === "hard-internal") return "warning";
  return "danger";
}

function decisionLabel(task: EvidenceTaskSummaryDto): "accepted" | "pending" | "unproven" {
  if (task.status === "done" && task.hasEvidenceArtifact) return "accepted";
  if (task.status === "review") return "pending";
  return "unproven";
}

function soc2StatusIntent(status: EvidenceSoc2PreflightStatusDto): Intent {
  if (status === "Verified") return "success";
  if (status === "Exception") return "danger";
  if (status === "Needs determination") return "warning";
  return "neutral";
}

function soc2CheckResultIntent(result: "pass" | "fail" | "unknown" | "not_run"): Intent {
  if (result === "pass") return "success";
  if (result === "fail") return "danger";
  if (result === "unknown") return "warning";
  return "neutral";
}

function soc2InventoryHealthIntent(health: "healthy" | "warning" | "blocked"): Intent {
  if (health === "healthy") return "success";
  if (health === "blocked") return "danger";
  return "warning";
}

function soc2RetentionIntent(state: "retained" | "expires_soon" | "expired"): Intent {
  if (state === "retained") return "success";
  if (state === "expired") return "danger";
  return "warning";
}

function soc2PolicyProposalIntent(status: "blocked" | "awaiting_activation" | "activated"): Intent {
  if (status === "activated") return "success";
  if (status === "blocked") return "danger";
  return "warning";
}

function soc2PolicyActionIntent(ok: boolean): Intent {
  return ok ? "success" : "warning";
}

function dogfoodIntent(
  freshness: UniversalConsoleOverview["dogfood"]["freshness"] | undefined,
): Intent {
  if (freshness === "FRESH") return "success";
  if (freshness === "FAILING") return "danger";
  if (freshness === "STALE") return "warning";
  return "neutral";
}

function pct(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "0%";
  return `${Math.round(value * 100)}%`;
}

function taskColumns(
  onOpen: (id: number) => void,
): DataTableColumn<EvidenceTaskSummaryDto>[] {
  return [
    {
      key: "id",
      header: "#",
      render: (r) => (
        <button
          type="button"
          className={`${NUM} text-primary hover:underline`}
          onClick={() => onOpen(r.id)}
        >
          {r.id}
        </button>
      ),
    },
    {
      key: "title",
      header: "Workflow run",
      render: (r) => (
        <button
          type="button"
          className="max-w-[34rem] text-left text-foreground hover:underline"
          onClick={() => onOpen(r.id)}
        >
          <span className="line-clamp-2">{r.title}</span>
        </button>
      ),
    },
    {
      key: "decision",
      header: "Decision",
      render: (r) => {
        const label = decisionLabel(r);
        return (
          <IntentBadge
            intent={
              label === "accepted"
                ? "success"
                : label === "pending"
                  ? "warning"
                  : "neutral"
            }
          >
            {label}
          </IntentBadge>
        );
      },
    },
    {
      key: "gate",
      header: "Actor -> verifier",
      render: (r) => (
        <span className={MUTED}>
          {r.ownerLabel ?? "actor unknown"} {"->"} {r.verifierLabel ?? "awaiting verifier"}
        </span>
      ),
    },
    {
      key: "consequence",
      header: "Consequence",
      render: (r) => (
        <IntentBadge intent={consequenceIntent(r.consequenceTag)}>
          {r.consequenceTag ?? "none"}
        </IntentBadge>
      ),
    },
    {
      key: "proof",
      header: "Proof",
      render: (r) => (
        <span className={MUTED}>
          <span className={NUM}>{r.claimCount}</span> claims ·{" "}
          <span className={NUM}>{r.receiptCount}</span> receipts
        </span>
      ),
    },
    {
      key: "artifact",
      header: "Evidence",
      render: (r) =>
        r.hasEvidenceArtifact ? (
          <IntentBadge intent={r.hasBundle ? "success" : "warning"}>
            {r.hasBundle ? "pack ready" : "artifact"}
          </IntentBadge>
        ) : (
          <span className={MUTED}>not emitted</span>
        ),
    },
  ];
}

function ReadinessStrip({
  silent,
  tasks,
  readiness,
}: {
  silent: SilentApproveDto | undefined;
  tasks: EvidenceTaskSummaryDto[];
  readiness: EvidenceReadinessDto | undefined;
}) {
  const hasEvidence = tasks.some((t) => t.hasEvidenceArtifact);
  const hasReview = tasks.some((t) => t.status === "review");
  const hasClaims = tasks.some((t) => t.claimCount > 0);
  const liveItems: ReadinessTile[] | undefined = readiness?.items.slice(0, 4).map((item) => ({
    label: item.label,
    value:
      item.status === "ready"
        ? "ready"
        : item.status === "blocked"
          ? "blocked"
          : "watch",
    intent:
      item.status === "ready"
        ? "success"
        : item.status === "blocked"
          ? "danger"
          : "warning",
    detail: item.detail,
  }));
  const items: ReadinessTile[] = liveItems ?? [
    {
      label: "Review door",
      value: hasReview ? "work waiting" : "clear",
      intent: hasReview ? "warning" : "success",
      detail: "local evidence rows",
    },
    {
      label: "Claim ingest",
      value: hasClaims ? "live rows" : "waiting",
      intent: hasClaims ? "success" : "warning",
      detail: "asserted workflow claims",
    },
    {
      label: "Evidence packs",
      value: hasEvidence ? "available" : "none yet",
      intent: hasEvidence ? "success" : "warning",
      detail: "artifact and bundle state",
    },
    {
      label: "Silent approve",
      value: silent ? silent.ratePct : "measuring",
      intent: silent ? (silent.ok ? "success" : "danger") : "neutral",
      detail: "override.human path",
    },
  ];

  return (
    <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => (
        <div key={item.label} className="bg-card px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <span className="type-subtext text-muted-foreground">{item.label}</span>
            <IntentBadge intent={item.intent}>{item.value}</IntentBadge>
          </div>
          <div className="mt-1 truncate type-subtext text-muted-foreground">{item.detail}</div>
        </div>
      ))}
    </div>
  );
}

/** Exported so the verdict-vocabulary gate can render this reader against a real receipt
 *  set rather than assert on its source text. */
export function D1VerificationLoopPanel({
  tasks,
  workflowCandidates,
  readiness,
  universalOverview,
  now = new Date().toISOString(),
}: {
  tasks: EvidenceTaskSummaryDto[];
  workflowCandidates: EvidenceWorkflowCandidateDto[];
  readiness: EvidenceReadinessDto | undefined;
  universalOverview?: UniversalConsoleOverview;
  /** The instant every stored verdict is read against. Injected so a test can stand on either side of a deadline. */
  now?: string;
}) {
  const canonicalWork = universalOverview?.current.work ?? [];
  const canonicalContracts = universalOverview?.current.contracts ?? [];
  const canonicalAttempts = universalOverview?.current.attempts ?? [];
  const canonicalReceipts = universalOverview?.current.receipts ?? [];
  // READ TIME, through the one law. A signed receipt cannot be settled in
  // place, so a PENDING one keeps saying PENDING past its deadline; every
  // count, note and stage below is derived from THIS array, so resolving it
  // here is the whole wiring for this panel — and the reason the panel itself
  // owns no deadline arithmetic.
  const currentReceiptStatuses = canonicalReceipts.map(
    (receipt) => resolveStoredVerdict({ verdict: receipt.payload, now }).status,
  );
  const currentWorkWithReceipts = new Set(
    canonicalReceipts.map((receipt) => receipt.payload.binding.workItemId),
  );
  const dogfood = universalOverview?.dogfood;
  const dogfoodFresh = dogfood?.freshness === "FRESH";
  const dogfoodLatestRun = dogfood?.latestRun;
  const admittedCandidates = workflowCandidates.filter((candidate) => candidate.status === "admit");
  const rejectedCandidates = workflowCandidates.filter((candidate) => candidate.status === "reject");
  const needsOracleCandidates = workflowCandidates.filter((candidate) => candidate.status === "needs-oracle");
  const tasksWithClaims = tasks.filter((task) => task.claimCount > 0);
  const tasksWithReceipts = tasks.filter((task) => task.receiptCount > 0);
  const tasksWithEvidence = tasks.filter((task) => task.hasEvidenceArtifact || task.hasBundle);
  const acceptedWithEvidence = tasks.filter(
    (task) => task.status === "done" && (task.hasEvidenceArtifact || task.hasBundle),
  );
  const bundledAccepted = acceptedWithEvidence.filter((task) => task.hasBundle);
  const recentFailures = readiness?.recentFailures ?? [];
  const compiler = readiness?.compiler;
  const compilerReady = compiler?.status === "ready";
  // A verdict that REJECTS an output is a finding that is not PASS. MISMATCH is a strict
  // refinement of FAIL (packages/core/src/verification/verdict-law.ts, 06ec7ece0): the
  // accusation rather than the refusal to confirm. Keying this on the literal "FAIL" alone
  // made the loop's most damning receipt — an independent record contradicting the claim —
  // read as "no wrong-output rejection is visible yet".
  const hasWrongOutputRejection =
    currentReceiptStatuses.includes("MISMATCH") ||
    currentReceiptStatuses.includes("FAIL") ||
    recentFailures.length > 0 ||
    rejectedCandidates.length > 0;
  // UNKNOWN reaches this surface for an adapter that HOLDS a record but cannot decide
  // (843fcf3de). It is NOT a rejection and must never satisfy the stage — but a receipt
  // that reached no finding is not the same fact as no receipt at all, so it is named.
  const noFindingReceiptCount = currentReceiptStatuses.filter(
    (status) => status === "UNKNOWN" || status === "NOT_RUN",
  ).length;
  // PENDING is a FOURTH bucket, deliberately not added to the filter above. A
  // receipt that is not yet due did not "reach no finding" — a finding is still
  // expected, by a stated deadline — and telling a reader the verifier could
  // not tell would send them looking for a broken instrument that is not
  // broken. It is equally not a rejection, so it does not reach
  // `hasWrongOutputRejection` either.
  const pendingReceiptCount = currentReceiptStatuses.filter(
    (status) => status === "PENDING",
  ).length;
  const noFindingNote =
    (noFindingReceiptCount
      ? ` ${noFindingReceiptCount} current receipt${noFindingReceiptCount === 1 ? "" : "s"} reached no finding (UNKNOWN/NOT_RUN): the verifier could not tell, which is not a rejection.`
      : "") +
    (pendingReceiptCount
      ? ` ${pendingReceiptCount} current receipt${pendingReceiptCount === 1 ? "" : "s"} are NOT YET DUE (PENDING): a finding is still expected by a stated deadline, which is neither a rejection nor a failure to decide.`
      : "");
  const hasRepairEvidence =
    currentReceiptStatuses.includes("PASS") || acceptedWithEvidence.length > 0;
  const hasCanonicalBundle = canonicalReceipts.length > 0;
  const latestTask = tasks[0] ?? null;
  const improvementTarget =
    canonicalReceipts[0]?.payload.binding.workItemId ??
    recentFailures[0]?.command ??
    rejectedCandidates[0]?.rejectionReason ??
    needsOracleCandidates[0]?.oracle ??
    latestTask?.title ??
    "waiting for first current Attempt";
  const blockers: D1LoopBlocker[] = [
    admittedCandidates.length === 0
      ? {
          label: "Authorized registration",
          detail: "No admitted workflow candidate is visible from the workflow-candidates endpoint.",
          href: "#workflow-discovery",
          action: "Review candidates",
        }
      : null,
    canonicalWork.length === 0 && tasksWithClaims.length === 0
      ? {
          label: "Task observation",
          detail: "No canonical Work row or evidence task in the current queue has captured claim rows.",
          href: "#decision-queue",
          action: "Open queue",
        }
      : null,
    !compilerReady
      ? {
          label: "Local model contract",
          detail: compiler
            ? `${compiler.model}: ${compiler.detail}`
            : "The readiness endpoint has not returned compiler state.",
          href: "/settings/integrations",
          action: "Open source review",
        }
      : null,
    canonicalReceipts.length === 0 && tasksWithReceipts.length === 0
      ? {
          label: "Attempt receipt",
          detail: "No current canonical Receipt or legacy task receipt is visible.",
          href: "#decision-queue",
          action: "Inspect receipts",
        }
      : null,
    !hasWrongOutputRejection
      ? {
          label: "Wrong-output rejection",
          detail: `No failed check receipt or rejected workflow candidate is visible.${noFindingNote}`,
          href: "/agents/verification?tab=rejects",
          action: "Open rejects",
        }
      : null,
    !hasRepairEvidence
      ? {
          label: "Repair verification",
          detail: "No accepted task with retained evidence is visible after a rejection.",
          href: "#decision-queue",
          action: "Open repair queue",
        }
      : null,
    !hasCanonicalBundle && bundledAccepted.length === 0
      ? {
          label: "Evidence retention",
          detail: "No current canonical receipt or accepted evidence pack bundle is exposed yet.",
          href: "/agents/verification?tab=receipts",
          action: "Open receipts",
        }
      : null,
    !dogfoodFresh
      ? {
          label: "Dogfood canary",
          detail: dogfood
            ? `${dogfood.freshness.toLowerCase()}: ${dogfood.reasonCodes.join(", ") || "canary needs a fresh passing run"}`
            : "The universal overview has not returned dogfood canary state.",
          href: "/agents/verification?tab=receipts",
          action: "Check canary proof",
        }
      : null,
  ].filter((blocker): blocker is D1LoopBlocker => blocker !== null);
  const stages: D1LoopStage[] = [
    {
      key: "registration",
      label: "Authorized agent",
      value: admittedCandidates.length > 0 ? `${admittedCandidates.length} admitted` : "unresolved",
      intent: admittedCandidates.length > 0 ? "success" : "warning",
      ready: admittedCandidates.length > 0,
      detail:
        admittedCandidates.length > 0
          ? `Latest admitted candidate: ${admittedCandidates[0]?.actor ?? "unknown actor"}.`
          : "No current authorized registration record is visible here yet.",
      href: "#workflow-discovery",
      action: "Review candidates",
    },
    {
      key: "task",
      label: "Task observation",
      value:
        canonicalWork.length > 0
          ? `${canonicalWork.length} canonical`
          : tasksWithClaims.length > 0
            ? `${tasksWithClaims.length} with claims`
            : "unresolved",
      intent: canonicalWork.length > 0 || tasksWithClaims.length > 0 ? "success" : "warning",
      ready: canonicalWork.length > 0 || tasksWithClaims.length > 0,
      detail:
        canonicalWork.length > 0
          ? `Latest canonical Work: ${canonicalWork[0]?.id}.`
          : tasksWithClaims.length > 0
          ? `Latest claim-bearing run: #${tasksWithClaims[0]?.id}.`
          : "No real task intent or activity has been captured as bound claim rows.",
      href: "#decision-queue",
      action: "Open queue",
    },
    {
      key: "contract",
      label: "Local contract",
      value:
        canonicalContracts.length > 0
          ? `${canonicalContracts.length} accepted`
          : compilerReady
            ? "compiler ready"
            : (compiler?.status ?? "unresolved"),
      intent:
        canonicalContracts.length > 0 || compilerReady
          ? "success"
          : compiler?.status === "blocked"
            ? "danger"
            : "warning",
      ready: canonicalContracts.length > 0 || compilerReady,
      detail: canonicalContracts.length > 0
        ? `Latest accepted Contract digest: ${canonicalContracts[0]?.contractDigest}.`
        : compiler
        ? `${compiler.model}: ${compiler.detail}`
        : "Source-derived contract readiness has not loaded.",
      href: "/settings/integrations",
      action: "Open source review",
    },
    {
      key: "attempt",
      label: "Attempt evidence",
      value:
        canonicalAttempts.length > 0
          ? `${canonicalAttempts.length} attempts`
          : tasksWithReceipts.length > 0
            ? `${tasksWithReceipts.length} with receipts`
            : "unresolved",
      intent: canonicalAttempts.length > 0 || tasksWithReceipts.length > 0 ? "success" : "warning",
      ready: canonicalAttempts.length > 0 || tasksWithReceipts.length > 0,
      detail:
        canonicalAttempts.length > 0
          ? `Latest canonical Attempt: ${canonicalAttempts[0]?.binding.attemptId}.`
          : tasksWithReceipts.length > 0
          ? "At least one run has compatibility receipts. Confirm detail pages bind them to current canonical Attempts before claiming D1."
          : "No current Attempt receipts are visible in the decision queue.",
      href: "#decision-queue",
      action: "Inspect receipts",
    },
    {
      key: "wrong-output",
      label: "Wrong output",
      // MISMATCH is tested FIRST and named separately: it is the more severe verdict, and a
      // reader who sees only "canonical FAIL" cannot tell a demonstrated violation from a
      // coverage hole. Every other arm of the ternary keeps its exact prior text.
      value: hasWrongOutputRejection
        ? currentReceiptStatuses.includes("MISMATCH")
          ? "canonical MISMATCH"
          : currentReceiptStatuses.includes("FAIL")
          ? "canonical FAIL"
          : recentFailures.length > 0
          ? `${recentFailures.length} rejected`
          : `${rejectedCandidates.length} rejected`
        : "unresolved",
      intent: hasWrongOutputRejection ? "danger" : "warning",
      ready: hasWrongOutputRejection,
      detail:
        currentReceiptStatuses.includes("MISMATCH")
          ? `A current universal receipt records a MISMATCH verdict: an independent record was consulted and it contradicts the claim.${noFindingNote}`
          : currentReceiptStatuses.includes("FAIL")
          ? `A current universal receipt records a FAIL verdict.${noFindingNote}`
          : recentFailures.length > 0
          ? `Latest failed check: ${recentFailures[0]?.command ?? "unknown command"}.${noFindingNote}`
          : rejectedCandidates.length > 0
            ? `Latest rejected candidate: ${rejectedCandidates[0]?.rejectionReason ?? "reason retained"}.${noFindingNote}`
            : `No deliberate wrong-output rejection is visible yet.${noFindingNote}`,
      href: "/agents/verification?tab=rejects",
      action: "Open rejects",
    },
    {
      key: "repair",
      label: "Repair verified",
      value: hasRepairEvidence
        ? currentReceiptStatuses.includes("PASS")
          ? "canonical PASS"
          : `${acceptedWithEvidence.length} accepted`
        : "unresolved",
      intent: hasRepairEvidence ? "success" : "warning",
      ready: hasRepairEvidence,
      detail: currentReceiptStatuses.includes("PASS")
        ? `A current universal receipt records a PASS verdict for ${[...currentWorkWithReceipts][0] ?? "current Work"}.`
        : hasRepairEvidence
        ? `Accepted evidence is visible on run #${acceptedWithEvidence[0]?.id}. Confirm it follows the rejected attempt under unchanged criteria.`
        : "No repaired output has been accepted with retained evidence after the rejection.",
      href: "#decision-queue",
      action: "Open repair queue",
    },
    {
      key: "evidence",
      label: "Durable evidence",
      value:
        canonicalReceipts.length > 0
          ? `${canonicalReceipts.length} receipts`
          : bundledAccepted.length > 0
            ? `${bundledAccepted.length} bundles`
            : "unresolved",
      intent: canonicalReceipts.length > 0 || bundledAccepted.length > 0 ? "success" : "warning",
      ready: canonicalReceipts.length > 0 || bundledAccepted.length > 0,
      detail:
        canonicalReceipts.length > 0
          ? "Current universal receipts are retained with contract, attempt and evidence graph bindings."
          : bundledAccepted.length > 0
          ? "Accepted evidence packs are retained for replay and recovery inspection."
          : tasksWithEvidence.length > 0
            ? "Artifacts are visible, but no accepted evidence pack bundle is exposed yet."
            : "No evidence pack or artifact is visible for the current D1 loop.",
      href: "#decision-queue",
      action: "Open evidence",
    },
    {
      key: "dogfood-canary",
      label: "Dogfood canary",
      value: dogfood?.freshness.toLowerCase() ?? "unresolved",
      intent: dogfoodIntent(dogfood?.freshness),
      ready: dogfoodFresh,
      detail: dogfoodFresh
        ? `Latest run ${dogfoodLatestRun?.id ?? "unknown"} passed with current completion metadata and retained receipt proof.`
        : dogfood
        ? `Latest state: ${dogfood.freshness.toLowerCase()}; ${dogfood.reasonCodes.join(", ") || "fresh passing canary not visible"}.`
        : "No dogfood status has loaded from the universal overview.",
      href: "/agents/verification?tab=receipts",
      action: "Check canary proof",
    },
  ];
  const nextStage = stages.find((stage) => !stage.ready);
  const canonicalReady =
    admittedCandidates.length > 0 &&
    (canonicalWork.length > 0 || tasksWithClaims.length > 0) &&
    (canonicalContracts.length > 0 || compilerReady) &&
    (canonicalAttempts.length > 0 || tasksWithReceipts.length > 0) &&
    hasWrongOutputRejection &&
    hasRepairEvidence &&
    (canonicalReceipts.length > 0 || bundledAccepted.length > 0);

  return (
    <Card id="d1-verification-loop" data-d1-verification-loop>
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>D1 verification loop</CardTitle>
            <CardDescription>
              Current product-visible state for registration, task observation, contract derivation, wrong-output rejection, repaired Attempt verification, retained evidence, and the next improvement.
            </CardDescription>
          </div>
          <IntentBadge intent={canonicalReady ? "success" : "warning"}>
            {canonicalReady ? "evidence visible" : "unresolved gates"}
          </IntentBadge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/15 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <IntentBadge intent={dogfoodIntent(dogfood?.freshness)}>
                dogfood {dogfood?.freshness.toLowerCase() ?? "loading"}
              </IntentBadge>
              <span className="type-subtext text-muted-foreground">
                {dogfoodLatestRun
                  ? `latest ${dogfoodLatestRun.id} · ${dogfoodLatestRun.status}`
                  : "no current canary run"}
              </span>
            </div>
            <p className="mt-2 type-body leading-5 text-muted-foreground">
              {nextStage
                ? `Next visible step: ${nextStage.label}. ${nextStage.detail}`
                : "Every D1 step exposed by the current UI payload is visible. Use the latest receipt and evidence pack to review the next product change."}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {nextStage?.href ? (
              <Button size="sm" asChild>
                <Link href={nextStage.href}>
                  <Icon name="arrow-right" size="sm" />
                  {nextStage.action ?? "Open"}
                </Link>
              </Button>
            ) : null}
            <Button size="sm" variant="outline" asChild>
              <Link href="/agents/verification?tab=receipts">
                <Icon name="shield-check" size="sm" />
                Receipts
              </Link>
            </Button>
          </div>
        </div>
        <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-2 xl:grid-cols-3">
          {stages.map((stage) => (
            <a
              key={stage.key}
              href={stage.href}
              className="block min-h-[8.25rem] bg-card p-3 text-current no-underline hover:bg-muted/30"
              data-d1-stage={stage.key}
              data-d1-state={stage.value}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
                    {stage.label}
                  </div>
                  <div className="mt-1 type-body font-medium text-foreground">{stage.value}</div>
                </div>
                <IntentBadge intent={stage.intent}>{stage.intent}</IntentBadge>
              </div>
              <p className="mt-2 line-clamp-3 type-subtext leading-5 text-muted-foreground">
                {stage.detail}
              </p>
              {stage.action ? (
                <div className="mt-2 type-subtext font-medium text-primary">
                  {stage.action}
                </div>
              ) : null}
            </a>
          ))}
        </div>
        <div className="grid gap-3 type-body lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="rounded-lg border border-border bg-muted/15 p-3">
            <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
              Unresolved blockers
            </div>
            {blockers.length > 0 ? (
              <ul className="mt-2 grid gap-2">
                {blockers.map((blocker) => (
                  <li key={blocker.label} className="grid gap-1">
                    <div className="type-body font-medium text-foreground">{blocker.label}</div>
                    <div className="type-subtext leading-5 text-muted-foreground">{blocker.detail}</div>
                    <Link href={blocker.href} className="type-subtext font-medium text-primary hover:underline">
                      {blocker.action}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-muted-foreground">
                No unresolved D1 blockers are visible in the current readiness and queue payloads.
              </p>
            )}
          </div>
          <div className="rounded-lg border border-border bg-muted/15 p-3">
            <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
              Improvement mark
            </div>
            <p className="mt-1 text-muted-foreground">
              Next repair target: {improvementTarget}. The repaired system still needs a fresh Attempt and verifier receipt before it can dogfood the next iteration.
            </p>
            <p className="mt-2 type-subtext leading-5 text-muted-foreground">
              Legacy rows, compatibility receipts, model runtime availability, and workflow candidates remain non-canonical until a current Contract, Attempt, evidence bundle, and receipt bind the same work.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Soc2PreflightPanel({
  report,
  importOnboardingState,
  policyStudioState,
  batchJobs,
  runs,
  onImported,
}: {
  report: EvidenceSoc2PreflightReportDto | undefined;
  importOnboardingState: EvidenceSoc2PreflightImportOnboardingStateDto | undefined;
  policyStudioState: EvidenceSoc2PreflightPolicyStudioStateDto | undefined;
  batchJobs: EvidenceSoc2PreflightBatchJobDto[];
  runs: EvidenceSoc2PreflightRunSummaryDto[];
  onImported: () => void;
}) {
  const [saving, setSaving] = React.useState(false);
  const [batching, setBatching] = React.useState(false);
  const [exportingId, setExportingId] = React.useState<string | null>(null);
  const [importing, setImporting] = React.useState(false);
  const [reviewingId, setReviewingId] = React.useState<string | null>(null);
  const [reviewDetailId, setReviewDetailId] = React.useState<string | null>(null);
  const [correctingId, setCorrectingId] = React.useState<string | null>(null);
  const [mappingId, setMappingId] = React.useState<string | null>(null);
  const [replayingId, setReplayingId] = React.useState<string | null>(null);
  const [scorecardId, setScorecardId] = React.useState<string | null>(null);
  const [inventoryId, setInventoryId] = React.useState<string | null>(null);
  const [auditPacketId, setAuditPacketId] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [batchError, setBatchError] = React.useState<string | null>(null);
  const [exportError, setExportError] = React.useState<string | null>(null);
  const [auditPacketError, setAuditPacketError] = React.useState<string | null>(null);
  const [importError, setImportError] = React.useState<string | null>(null);
  const [reviewError, setReviewError] = React.useState<string | null>(null);
  const [reviewDetailError, setReviewDetailError] = React.useState<string | null>(null);
  const [savedId, setSavedId] = React.useState<string | null>(null);
  const [batchJob, setBatchJob] = React.useState<EvidenceSoc2PreflightBatchJobDto | null>(null);
  const [batchMessage, setBatchMessage] = React.useState<string | null>(null);
  const [reviewMessage, setReviewMessage] = React.useState<string | null>(null);
  const [correctionMessage, setCorrectionMessage] = React.useState<string | null>(null);
  const [mappingMessage, setMappingMessage] = React.useState<string | null>(null);
  const [replayMessage, setReplayMessage] = React.useState<string | null>(null);
  const [scorecard, setScorecard] = React.useState<EvidenceSoc2Phase1ScorecardDto | null>(null);
  const [scorecardError, setScorecardError] = React.useState<string | null>(null);
  const [evidenceInventory, setEvidenceInventory] =
    React.useState<EvidenceSoc2EvidenceInventoryDto | null>(null);
  const [inventoryError, setInventoryError] = React.useState<string | null>(null);
  const [sourceAlerts, setSourceAlerts] =
    React.useState<EvidenceSoc2SourceAlertReportDto | null>(null);
  const [sourceAlertsId, setSourceAlertsId] = React.useState<string | null>(null);
  const [sourceAlertsError, setSourceAlertsError] = React.useState<string | null>(null);
  const [notificationOutbox, setNotificationOutbox] =
    React.useState<EvidenceSoc2NotificationOutboxDto | null>(null);
  const [notificationOutboxId, setNotificationOutboxId] = React.useState<string | null>(null);
  const [notificationOutboxError, setNotificationOutboxError] = React.useState<string | null>(null);
  const [scheduleReport, setScheduleReport] =
    React.useState<EvidenceSoc2ScheduleReportDto | null>(null);
  const [scheduleReportId, setScheduleReportId] = React.useState<string | null>(null);
  const [scheduleReportError, setScheduleReportError] = React.useState<string | null>(null);
  const [auditPacket, setAuditPacket] =
    React.useState<EvidenceSoc2AuditPreparationExportPacketDto | null>(null);
  const [reviewQueue, setReviewQueue] = React.useState<EvidenceSoc2ReviewQueueDto | null>(null);
  const [semanticWorkerReview, setSemanticWorkerReview] =
    React.useState<EvidenceSoc2SemanticWorkerReviewDto | null>(null);
  const [reviewDetailItem, setReviewDetailItem] =
    React.useState<EvidenceSoc2ReviewQueueItemDto | null>(null);
  const [reviewDetailRun, setReviewDetailRun] =
    React.useState<EvidenceSoc2PreflightStoredRunDto | null>(null);
  const [reviewDecisionRationaleDraft, setReviewDecisionRationaleDraft] =
    React.useState<string>("");
  const [remediations, setRemediations] = React.useState<EvidenceSoc2RemediationDto | null>(null);
  const [queueError, setQueueError] = React.useState<string | null>(null);
  const [selectedControlId, setSelectedControlId] = React.useState<string | null>(null);
  const [importLabel, setImportLabel] = React.useState<string>("");
  const [importOrganizationId, setImportOrganizationId] = React.useState<string>(
    report?.organization.id ?? "",
  );
  const [importOrganizationName, setImportOrganizationName] = React.useState<string>(
    report?.organization.name ?? "",
  );
  const [importAuditPeriodId, setImportAuditPeriodId] = React.useState<string>(
    report?.auditPeriod.id ?? "",
  );
  const [importAuditPeriodStart, setImportAuditPeriodStart] = React.useState<string>(
    report?.auditPeriod.start ?? "",
  );
  const [importAuditPeriodEnd, setImportAuditPeriodEnd] = React.useState<string>(
    report?.auditPeriod.end ?? "",
  );
  const [importPackage, setImportPackage] = React.useState<EvidenceSoc2PreflightPackageDto | null>(null);
  const [importPreview, setImportPreview] = React.useState<EvidenceSoc2PreflightReportDto | null>(null);
  const [importKind, setImportKind] = React.useState<"json" | "csv" | "xlsx" | null>(null);
  const [importCsv, setImportCsv] = React.useState<string | null>(null);
  const [importXlsxBase64, setImportXlsxBase64] = React.useState<string | null>(null);
  const [importIssues, setImportIssues] = React.useState<{
    errors: EvidenceSoc2CsvParseIssueDto[];
    warnings: EvidenceSoc2CsvParseIssueDto[];
  }>({ errors: [], warnings: [] });
  const [exportEvidenceRefsDraft, setExportEvidenceRefsDraft] = React.useState<string>("");
  const [exportIncludeExpiredEvidence, setExportIncludeExpiredEvidence] = React.useState(false);
  const controlColumns = React.useMemo<DataTableColumn<EvidenceSoc2PreflightReportDto["controls"][number]>[]>(
    () => [
      {
        key: "control",
        header: "Control",
        render: (row) => (
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <code className="type-subtext text-muted-foreground">{row.controlId}</code>
              <IntentBadge intent={soc2StatusIntent(row.status)}>{row.status}</IntentBadge>
            </div>
            <div className="line-clamp-1 type-body font-medium">{row.title}</div>
            <div className="type-subtext text-muted-foreground">{row.tsc} · {row.family}</div>
          </div>
        ),
      },
      {
        key: "owner",
        header: "Owner",
        render: (row) => <span className="type-body">{row.owner}</span>,
      },
      {
        key: "evidence",
        header: "Evidence",
        render: (row) => (
          <span className={MUTED}>
            <span className={NUM}>{row.evidenceCount}</span> artifacts ·{" "}
            <span className={NUM}>{row.checkCount}</span> checks
          </span>
        ),
      },
      {
        key: "reason",
        header: "Reason",
        render: (row) => (
          <span className={`${MUTED} line-clamp-2`} title={row.reason}>
            {row.reason}
          </span>
        ),
      },
      {
        key: "inspect",
        header: "",
        render: (row) => (
          <Button
            size="sm"
            variant="outline"
            aria-label={`Inspect ${row.controlId}`}
            onClick={() => setSelectedControlId(row.controlId)}
          >
            <Icon name="search" size="sm" />
            Inspect
          </Button>
        ),
      },
    ],
    [],
  );
  const findingColumns = React.useMemo<DataTableColumn<EvidenceSoc2FindingDto>[]>(
    () => [
      {
        key: "status",
        header: "Status",
        render: (row) => <IntentBadge intent={soc2StatusIntent(row.status)}>{row.status}</IntentBadge>,
      },
      {
        key: "control",
        header: "Control",
        render: (row) => (
          <div className="space-y-1">
            <code className="type-subtext text-muted-foreground">{row.controlId}</code>
            <div className="type-subtext text-muted-foreground">{row.requirementId ?? "control-level"}</div>
          </div>
        ),
      },
      {
        key: "reason",
        header: "Finding",
        render: (row) => (
          <div className="space-y-1">
            <div className="line-clamp-2 type-body">{row.reason}</div>
            <div className="type-subtext text-muted-foreground">{row.action}</div>
          </div>
        ),
      },
      {
        key: "evidence",
        header: "Evidence",
        render: (row) => (
          <span className={MUTED}>
            {row.evidenceRefs.length > 0 ? row.evidenceRefs.slice(0, 2).join(", ") : "needs evidence"}
          </span>
        ),
      },
    ],
    [],
  );

  React.useEffect(() => {
    let cancelled = false;
    async function loadReviewState() {
      if (runs.length === 0) {
        setReviewQueue(null);
        setSemanticWorkerReview(null);
        setRemediations(null);
        setQueueError(null);
        return;
      }
      try {
        const [queue, semanticWorkerRows, remediationList] = await Promise.all([
          apiCall(evidence.soc2PreflightReviewQueue, {
            query: { limit: 6 },
          }),
          apiCall(evidence.soc2PreflightSemanticWorkerReview, {
            query: { limit: 6 },
          }),
          apiCall(evidence.soc2PreflightRemediations, {
            query: { limit: 6 },
          }),
        ]);
        if (!cancelled) {
          setReviewQueue(queue);
          setSemanticWorkerReview(semanticWorkerRows);
          setRemediations(remediationList);
          setQueueError(null);
        }
      } catch (e) {
        if (!cancelled) setQueueError(e instanceof Error ? e.message : String(e));
      }
    }
    void loadReviewState();
    return () => {
      cancelled = true;
    };
  }, [runs]);

  React.useEffect(() => {
    let cancelled = false;
    async function loadLatestScorecard() {
      const latestRun = runs[0];
      if (!latestRun) {
        setScorecard(null);
        setScorecardError(null);
        return;
      }
      setScorecardId(`latest:${latestRun.id}`);
      setScorecardError(null);
      try {
        const nextScorecard = await apiCall(evidence.soc2PreflightScorecard, {
          params: { id: latestRun.id },
          query: {},
        });
        if (!cancelled) setScorecard(nextScorecard);
      } catch (e) {
        if (!cancelled) setScorecardError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setScorecardId(null);
      }
    }
    void loadLatestScorecard();
    return () => {
      cancelled = true;
    };
  }, [runs]);

  if (!report) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>SOC 2 preflight</CardTitle>
          <CardDescription>Preparing representative control package status.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2">
            <div className="h-10 animate-pulse rounded-md bg-muted" />
            <div className="h-20 animate-pulse rounded-md bg-muted" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const summary = report.summary;
  const findings = report.findings.slice(0, 6);
  const selectedControl =
    report.controls.find((control) => control.controlId === selectedControlId) ??
    report.controls.find((control) => control.status !== "Verified") ??
    report.controls[0] ??
    null;
  const selectedReceiptHistory = selectedControl
    ? report.receipts.filter((receipt) => receipt.controlId === selectedControl.controlId)
    : [];
  const selectedPolicyAdmissionSignals = selectedControl
    ? [
        {
          label: "Requirements",
          value: `${selectedControl.requirements.length} mapped`,
          detail: "Human approval sees the control text, mapped requirement ids, and required checks before activation.",
          intent: selectedControl.requirements.length > 0 ? "success" : "warning",
        },
        {
          label: "Evidence inputs",
          value: `${selectedControl.policyEvaluation.checkRuns.reduce(
            (total, checkRun) => total + checkRun.evidenceRefs.length,
            0,
          )} refs`,
          detail: "Policy proposals must name evidence refs and observation ids instead of trusting summaries.",
          intent: selectedControl.evidenceRefs.length > 0 ? "success" : "warning",
        },
        {
          label: "Unknown routing",
          value: `${selectedControl.policyEvaluation.checkRuns.filter((checkRun) => checkRun.result === "unknown").length} unknown`,
          detail: "Ungrounded or missing evidence remains Needs determination and cannot silently become PASS.",
          intent: selectedControl.policyEvaluation.checkRuns.some((checkRun) => checkRun.result === "unknown")
            ? "warning"
            : "success",
        },
        {
          label: "Loosening impact",
          value: selectedControl.policyEvaluation.checkRuns.some((checkRun) => checkRun.result === "fail")
            ? "analysis required"
            : "no failing checks",
          detail: "Any change that would remove a failed required check needs frozen-case impact review.",
          intent: selectedControl.policyEvaluation.checkRuns.some((checkRun) => checkRun.result === "fail")
            ? "danger"
            : "neutral",
        },
        {
          label: "Activation",
          value: "human gate",
          detail: `Authorized reviewer activation would version ${selectedControl.policyEvaluation.policy.version} and preserve replay history.`,
          intent: "neutral",
        },
      ]
    : [];
  const selectedPolicyWorkflow =
    selectedControl && policyStudioState?.controlId === selectedControl.controlId
      ? policyStudioState.workflow
      : null;
  const selectedPolicyActions =
    selectedPolicyWorkflow && policyStudioState
      ? policyStudioState.actions.filter((action) =>
          selectedPolicyWorkflow.proposals.some((proposal) => proposal.proposalId === action.proposalId),
        )
      : [];
  const selectedPolicyStudioState = selectedPolicyWorkflow && policyStudioState ? policyStudioState : null;
  const visibleBatchJob = batchJob ?? batchJobs[0] ?? null;
  const reviewDetailControl =
    reviewDetailItem && reviewDetailRun
      ? reviewDetailRun.report.controls.find((control) => control.controlId === reviewDetailItem.controlId) ?? null
      : null;
  const reviewDetailCheck =
    reviewDetailItem?.checkId && reviewDetailControl
      ? reviewDetailControl.policyEvaluation.checkRuns.find((checkRun) => checkRun.checkId === reviewDetailItem.checkId) ?? null
      : null;
  const reviewDetailFinding =
    reviewDetailItem && reviewDetailControl
      ? reviewDetailControl.findings.find(
          (finding) =>
            finding.checkId === reviewDetailItem.checkId &&
            finding.requirementId === reviewDetailItem.requirementId,
        ) ??
        reviewDetailControl.findings.find((finding) => finding.checkId === reviewDetailItem.checkId) ??
        reviewDetailControl.findings[0] ??
        null
      : null;
  const reviewDetailEvidence =
    reviewDetailItem && reviewDetailControl
      ? reviewDetailControl.evidenceArtifacts.filter((artifact) =>
          reviewDetailItem.evidenceRefs.includes(artifact.id),
        )
      : [];
  const reviewDetailReceipt =
    reviewDetailItem && reviewDetailRun
      ? [...reviewDetailRun.report.receipts]
          .reverse()
          .find(
            (receipt) =>
              receipt.id === reviewDetailItem.receiptId ||
              receipt.controlId === reviewDetailItem.controlId,
          ) ?? null
      : null;
  const importPreviewControls = importPreview?.controls ?? [];
  const importPreviewTemplateMapped = importPreviewControls.filter(
    (control) => control.templateMapping?.templateId,
  ).length;
  const importPreviewFamilies = [
    ...new Set(importPreviewControls.map((control) => control.family)),
  ].sort();
  const importPreviewReady = Boolean(
    importPreview && importPackage && importIssues.errors.length === 0,
  );
  const importPreviewPrimaryFinding = importPreview?.findings[0] ?? null;
  const importPersistenceNextAction =
    importOnboardingState?.nextAction ?? "create-import";
  const persistedImportJobCount = importOnboardingState?.persistedJobCount ?? 0;
  const persistedMappingDraftCount = importOnboardingState?.mappingDraftCount ?? 0;
  const persistedReadyJobCount = importOnboardingState?.readyJobIds.length ?? 0;
  const persistedImportJobIds = [
    importOnboardingState?.latestJobId,
    ...runs
      .map((run) => run.id)
      .filter((id) => id !== importOnboardingState?.latestJobId),
  ]
    .filter((id): id is string => typeof id === "string" && id.length > 0)
    .slice(0, 3);
  const latestMappingDraftId = importOnboardingState?.latestDraftId ?? null;
  const importOnboardingBlockedReasons =
    importOnboardingState?.blockedReasons ?? [];
  const timeToFirstFindingLabel =
    importOnboardingState?.timeToFirstFindingMinutes != null
      ? `${importOnboardingState.timeToFirstFindingMinutes} min`
      : "not measured";
  const operationalEvidencePanelsOpen = [
    sourceAlerts ? "source health" : null,
    scheduleReport ? "refresh schedule" : null,
    notificationOutbox ? "notification outbox" : null,
    evidenceInventory ? "evidence inventory" : null,
  ].filter(Boolean);
  const operationalEvidenceBlockingSignals =
    (sourceAlerts?.summary.critical ?? 0) +
    (scheduleReport?.summary.blocked ?? 0) +
    (notificationOutbox?.summary.pendingDeliveries ?? 0) +
    (evidenceInventory?.summary.blockedSources ?? 0);
  const operationalEvidenceReadinessSignals = [
    [
      "Source alerts",
      sourceAlerts ? `${sourceAlerts.summary.totalAlerts} alerts` : "not opened",
      sourceAlerts?.summary.critical ? "danger" : sourceAlerts ? "success" : "neutral",
    ],
    [
      "Refresh schedule",
      scheduleReport ? `${scheduleReport.summary.affectedControls} affected` : "not opened",
      scheduleReport?.summary.blocked ? "danger" : scheduleReport ? "success" : "neutral",
    ],
    [
      "Delivery outbox",
      notificationOutbox
        ? `${notificationOutbox.summary.pendingDeliveries} pending`
        : "not opened",
      notificationOutbox?.summary.pendingDeliveries
        ? "warning"
        : notificationOutbox
          ? "success"
          : "neutral",
    ],
    [
      "Evidence inventory",
      evidenceInventory
        ? `${evidenceInventory.summary.totalArtifacts} artifacts`
        : "not opened",
      evidenceInventory?.summary.blockedSources ? "danger" : evidenceInventory ? "success" : "neutral",
    ],
  ];
  const continuousControlLoopSignals = [
    {
      label: "Source change",
      value: sourceAlerts
        ? `${sourceAlerts.summary.controlsAtRisk} controls at risk`
        : "waiting for alerts",
      detail: sourceAlerts
        ? "Stale or blocked sources are promoted from source health into control risk."
        : "Open source alerts to confirm whether connector health changed control state.",
      intent: sourceAlerts?.summary.critical ? "danger" : sourceAlerts ? "warning" : "neutral",
    },
    {
      label: "Notify owner",
      value: notificationOutbox
        ? `${notificationOutbox.summary.sourceAlerts} source alerts`
        : "outbox not opened",
      detail: notificationOutbox
        ? "Dedupe keys keep repeated source failures from creating noisy duplicate pings."
        : "Open the outbox to verify source alerts and remediation due events are deliverable.",
      intent: notificationOutbox?.summary.pendingDeliveries
        ? "warning"
        : notificationOutbox
          ? "success"
          : "neutral",
    },
    {
      label: "Recheck remediation",
      value: remediations
        ? `${remediations.summary.requiresReverification} need rerun`
        : "remediation not opened",
      detail: remediations
        ? "Closed fixes still require a fresh verification run before the finding disappears."
        : "Open remediation to confirm which findings require a new control check.",
      intent: remediations?.summary.requiresReverification
        ? "warning"
        : remediations
          ? "success"
          : "neutral",
    },
    {
      label: "Stale finding",
      value: scheduleReport
        ? `${scheduleReport.summary.affectedControls} affected`
        : "schedule not opened",
      detail: scheduleReport
        ? "Missed refreshes conservatively degrade affected controls until current evidence arrives."
        : "Open the refresh schedule to see which controls degrade when jobs miss SLA.",
      intent: scheduleReport?.summary.blocked
        ? "danger"
        : scheduleReport?.summary.missed
          ? "warning"
          : scheduleReport
            ? "success"
            : "neutral",
    },
  ];
  const reviewCaseManagementSignals = [
    {
      label: "Prioritized queue",
      value: reviewQueue
        ? `${reviewQueue.summary.totalActionable} actionable`
        : "queue not opened",
      detail: reviewQueue
        ? `${reviewQueue.summary.exceptions} exceptions, ${reviewQueue.summary.needsDetermination} needs determination, ${reviewQueue.summary.staleNotTested} stale or not tested.`
        : "Open the verification inbox to sort FAIL and UNKNOWN work before closing an audit period.",
      intent: reviewQueue?.summary.totalActionable
        ? "warning"
        : reviewQueue
          ? "success"
          : "neutral",
    },
    {
      label: "Owner and age filters",
      value: reviewQueue?.items[0]
        ? `${reviewQueue.items[0].owner} · ${Math.round(reviewQueue.items[0].ageHours)}h`
        : "owner pending",
      detail: reviewQueue?.items[0]
        ? `Highest priority item ${reviewQueue.items[0].controlId} keeps owner, severity, audit period, and age visible.`
        : "Queue rows must keep owner, severity, age, control, and audit-period filters visible.",
      intent: reviewQueue?.items[0]?.severity === "critical" ? "danger" : reviewQueue ? "warning" : "neutral",
    },
    {
      label: "Rationale context",
      value: reviewDetailItem ? "decision drafted" : "select finding",
      detail: reviewDetailItem
        ? "Decisions bind actor rationale to focused evidence, policy context, and receipt context."
        : "Inspect a finding before recording exception, evidence request, or remediation rationale.",
      intent: reviewDetailItem ? "success" : "neutral",
    },
    {
      label: "Re-verification after fix",
      value: remediations
        ? `${remediations.summary.requiresReverification} require rerun`
        : "remediation not opened",
      detail: remediations
        ? `${remediations.summary.investigationOpen} investigations remain open until a linked receipt proves the fix.`
        : "Closed remediation remains open until a new verification run links a receipt.",
      intent: remediations?.summary.requiresReverification
        ? "warning"
        : remediations
          ? "success"
          : "neutral",
    },
  ];
  const semanticWorkerBlocking = semanticWorkerReview?.summary.blocking ?? 0;
  const semanticWorkerRowsVisible = semanticWorkerReview?.rows ?? [];
  const semanticWorkerQueueLabel = semanticWorkerReview?.source === "workspace-semantic-worker-queue"
    ? "workspace semantic-worker queue"
    : "route-fed reviewer preview";
  const semanticWorkerReviewSignals = semanticWorkerReview
    ? [
        [
          "Dispatch",
          `${semanticWorkerReview.summary.readyToDispatch} ready`,
          semanticWorkerReview.summary.readyToDispatch > 0 ? "warning" : "success",
        ],
        [
          "Worker health",
          `${semanticWorkerReview.summary.workerBlocked} blocked`,
          semanticWorkerReview.summary.workerBlocked > 0 ? "danger" : "success",
        ],
        [
          "Review results",
          `${semanticWorkerReview.summary.resultReady} ready`,
          semanticWorkerReview.summary.resultReady > 0 ? "success" : "neutral",
        ],
        [
          "Release",
          semanticWorkerReview.releaseReady ? "ready" : "blocked",
          semanticWorkerReview.releaseReady ? "success" : "warning",
        ],
      ]
    : [];
  const reviewCaseManagementFilters = [
    reviewQueue?.items.some((item) => item.owner) ? "owner" : null,
    reviewQueue?.items.some((item) => item.severity) ? "severity" : null,
    reviewQueue?.items.some((item) => item.ageHours >= 0) ? "age" : null,
    reviewQueue?.items.some((item) => item.controlId) ? "control" : null,
    reviewQueue?.items.some((item) => item.auditPeriodId) ? "audit period" : null,
  ].filter(Boolean);
  const reviewerDisagreementCount =
    (remediations?.summary.investigationOpen ?? 0) +
    (remediations?.summary.evidenceRequested ?? 0);
  const auditRoomReadinessSignals = auditPacket
    ? [
        {
          label: "Read-only reviewer access",
          value: "preview scoped",
          detail:
            "External reviewer access stays read-only and scoped to the packet before any download handoff.",
          intent: "success",
        },
        {
          label: "Scoped packet",
          value: `${auditPacket.summary.scopedEvidenceRefs} evidence refs`,
          detail: auditPacket.exportPolicy.allowedEvidenceRefs
            ? "Permission-scoped evidence refs are the only refs visible in this packet."
            : "Full permitted packet is visible; retention exclusions still apply.",
          intent: auditPacket.exportPolicy.receiptsRedacted ? "warning" : "success",
        },
        {
          label: "Sample metadata",
          value: `${auditPacket.evidenceMetadataRefs.length} rows`,
          detail:
            "Sample evidence rows expose source, content hash, collection time, retention state, and linked controls.",
          intent: auditPacket.evidenceMetadataRefs.length > 0 ? "success" : "warning",
        },
        {
          label: "Public receipt verification",
          value: `${auditPacket.receipts.length} receipts`,
          detail:
            "Receipt hashes are visible for independent replay; signing metadata remains explicit instead of implied.",
          intent: auditPacket.receipts.length > 0 ? "success" : "warning",
        },
        {
          label: "Download log handoff",
          value: "log required",
          detail:
            "Audit-room downloads must create viewer, packet hash, scoped evidence, and receipt id log rows.",
          intent: "neutral",
        },
      ]
    : [];
  const importAuditContextComplete =
    importOrganizationId.trim().length > 0 &&
    importOrganizationName.trim().length > 0 &&
    importAuditPeriodId.trim().length > 0 &&
    importAuditPeriodStart.trim().length > 0 &&
    importAuditPeriodEnd.trim().length > 0;

  function importAuditContextBody() {
    return {
      organizationId: importOrganizationId.trim(),
      organizationName: importOrganizationName.trim(),
      auditPeriodId: importAuditPeriodId.trim(),
      auditPeriodStart: importAuditPeriodStart.trim(),
      auditPeriodEnd: importAuditPeriodEnd.trim(),
    };
  }

  function exportQuery() {
    const evidenceRefs = exportEvidenceRefsDraft
      .split(/[\s,]+/)
      .map((ref) => ref.trim())
      .filter(Boolean)
      .join(",");
    return {
      ...(evidenceRefs ? { evidenceRefs } : {}),
      ...(exportIncludeExpiredEvidence ? { includeExpiredEvidence: true } : {}),
    };
  }

  async function saveRepresentativeRun() {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiCall(evidence.soc2PreflightImportSample, {});
      setSavedId(res.run.id);
      onImported();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function batchRepresentativeRun() {
    setBatching(true);
    setBatchError(null);
    setBatchMessage(null);
    try {
      const res = await apiCall(evidence.soc2PreflightBatchSample, {
        body: { idempotencyKey: "ui-representative-soc2-preflight" },
      });
      setBatchJob(res.job);
      if (res.run) setSavedId(res.run.id);
      setBatchMessage(
        res.reused
          ? `Batch reused · ${res.job.progress.completedControls}/${res.job.progress.totalControls} controls`
          : `Batch ${res.job.status} · ${res.job.progress.completedControls}/${res.job.progress.totalControls} controls`,
      );
      onImported();
    } catch (e) {
      setBatchError(e instanceof Error ? e.message : String(e));
    } finally {
      setBatching(false);
    }
  }

  async function downloadExportPacket(run: EvidenceSoc2PreflightRunSummaryDto) {
    setExportingId(run.id);
    setExportError(null);
    try {
      const packet = await apiCall(evidence.soc2PreflightExport, {
        params: { id: run.id },
        query: exportQuery(),
      });
      const blob = new Blob([JSON.stringify(packet, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `soc2-preflight-${run.auditPeriodId}-${run.id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : String(e));
    } finally {
      setExportingId(null);
    }
  }

  async function loadAuditPacket(run: EvidenceSoc2PreflightRunSummaryDto) {
    setAuditPacketId(run.id);
    setAuditPacketError(null);
    try {
      const packet = await apiCall(evidence.soc2PreflightExport, {
        params: { id: run.id },
        query: exportQuery(),
      });
      setAuditPacket(packet);
    } catch (e) {
      setAuditPacketError(e instanceof Error ? e.message : String(e));
    } finally {
      setAuditPacketId(null);
    }
  }

  async function previewImportPackage(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) return;
    setImportError(null);
    setImportPreview(null);
    setImportPackage(null);
    setImportCsv(null);
    setImportXlsxBase64(null);
    setImportKind(null);
    setImportIssues({ errors: [], warnings: [] });
    setImportLabel(file.name.replace(/\.(json|csv|xlsx)$/i, ""));
    try {
      if (/\.csv$/i.test(file.name)) {
        if (!importAuditContextComplete) {
          throw new Error("Organization and audit period are required before previewing a CSV import.");
        }
        const text = await file.text();
        const preview = await apiCall(evidence.soc2PreflightCsvPreview, {
          body: {
            csv: text,
            label: file.name.replace(/\.csv$/i, ""),
            ...importAuditContextBody(),
          },
        });
        setImportKind("csv");
        setImportCsv(text);
        setImportPackage(preview.package);
        setImportPreview(preview.report);
        setImportIssues({ errors: preview.errors, warnings: preview.warnings });
        if (!preview.ok) {
          setImportError(`${preview.errors.length} parse error${preview.errors.length === 1 ? "" : "s"} found.`);
        }
        return;
      }
      if (/\.xlsx$/i.test(file.name)) {
        if (!importAuditContextComplete) {
          throw new Error("Organization and audit period are required before previewing an XLSX import.");
        }
        const bytes = new Uint8Array(await file.arrayBuffer());
        let binary = "";
        const chunkSize = 0x8000;
        for (let index = 0; index < bytes.length; index += chunkSize) {
          binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
        }
        const xlsxBase64 = btoa(binary);
        const preview = await apiCall(evidence.soc2PreflightXlsxPreview, {
          body: {
            xlsxBase64,
            label: file.name.replace(/\.xlsx$/i, ""),
            ...importAuditContextBody(),
          },
        });
        setImportKind("xlsx");
        setImportXlsxBase64(xlsxBase64);
        setImportPackage(preview.package);
        setImportPreview(preview.report);
        setImportIssues({ errors: preview.errors, warnings: preview.warnings });
        if (!preview.ok) {
          setImportError(`${preview.errors.length} parse error${preview.errors.length === 1 ? "" : "s"} found.`);
        }
        return;
      }
      const text = await file.text();
      const parsed = JSON.parse(text) as EvidenceSoc2PreflightPackageDto;
      const preview = await apiCall(evidence.soc2PreflightRun, {
        body: { package: parsed },
      });
      setImportKind("json");
      setImportPackage(parsed);
      setImportPreview(preview);
    } catch (e) {
      setImportError(e instanceof Error ? e.message : String(e));
    }
  }

  async function saveImportedPackage() {
    if (!importPackage && !importCsv && !importXlsxBase64) return;
    if ((importKind === "csv" || importKind === "xlsx") && !importAuditContextComplete) {
      setImportError("Organization and audit period are required before saving this import.");
      return;
    }
    setImporting(true);
    setImportError(null);
    try {
      const res =
        importKind === "csv" && importCsv
          ? await apiCall(evidence.soc2PreflightCsvImport, {
              body: {
                csv: importCsv,
                label: importLabel.trim() || undefined,
                ...importAuditContextBody(),
              },
            })
          : importKind === "xlsx" && importXlsxBase64
            ? await apiCall(evidence.soc2PreflightXlsxImport, {
                body: {
                  xlsxBase64: importXlsxBase64,
                  label: importLabel.trim() || undefined,
                  ...importAuditContextBody(),
                },
              })
          : await apiCall(evidence.soc2PreflightImport, {
              body: {
                package: importPackage!,
                label: importLabel.trim() || undefined,
              },
            });
      setSavedId(res.run.id);
      onImported();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : String(e));
    } finally {
      setImporting(false);
    }
  }

  async function recordFirstReviewDecision(run: EvidenceSoc2PreflightRunSummaryDto) {
    setReviewingId(run.id);
    setReviewError(null);
    setReviewMessage(null);
    try {
      const stored = await apiCall(evidence.soc2PreflightGet, {
        params: { id: run.id },
        query: {},
      });
      const finding = stored.report.findings[0];
      if (!finding) {
        throw new Error("This run has no actionable finding to review.");
      }
      const response = await apiCall(evidence.soc2PreflightReviewDecision, {
        params: { id: run.id },
        body: {
          controlId: finding.controlId,
          findingId: [finding.controlId, finding.requirementId ?? "control", finding.checkId ?? "control"].join(":"),
          decision: finding.status === "Exception" ? "record_exception" : "request_evidence",
          actor: "soc2-reviewer",
          rationale:
            finding.status === "Exception"
              ? "Reviewer recorded the exception for audit-preparation follow-up."
              : "Reviewer requested clearer source evidence before relying on this control.",
          evidenceRefs: finding.evidenceRefs,
        },
      });
      setReviewMessage(`Decision recorded · receipt ${response.receipt.id}`);
      onImported();
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setReviewingId(null);
    }
  }

  async function loadReviewDetail(item: EvidenceSoc2ReviewQueueItemDto) {
    setReviewDetailId(item.id);
    setReviewDetailError(null);
    setReviewDetailItem(item);
    setReviewDecisionRationaleDraft(reviewDecisionRationale(item, "request_evidence"));
    try {
      const stored = await apiCall(evidence.soc2PreflightGet, {
        params: { id: item.runId },
        query: {},
      });
      setReviewDetailRun(stored);
    } catch (e) {
      setReviewDetailError(e instanceof Error ? e.message : String(e));
      setReviewDetailRun(null);
    } finally {
      setReviewDetailId(null);
    }
  }

  function reviewDecisionRationale(
    item: EvidenceSoc2ReviewQueueItemDto,
    decision: EvidenceSoc2ReviewDecisionKindDto,
  ): string {
    if (decision === "accept") {
      return "Reviewer accepted this finding after inspecting the focused evidence, policy check, and receipt context.";
    }
    if (decision === "record_exception") {
      return "Reviewer recorded this exception after inspecting the focused evidence, policy check, and receipt context.";
    }
    if (decision === "investigate_remediate") {
      return "Reviewer opened remediation because the focused evidence and check context require investigation.";
    }
    return item.status === "Stale/not tested"
      ? "Reviewer requested refreshed evidence because the focused review detail shows stale or missing support."
      : "Reviewer requested clearer source evidence after inspecting the focused review detail.";
  }

  async function recordQueueReviewDecision(
    item: EvidenceSoc2ReviewQueueItemDto,
    decision?: EvidenceSoc2ReviewDecisionKindDto,
    rationale?: string,
  ) {
    setReviewingId(item.runId);
    setReviewError(null);
    setReviewMessage(null);
    try {
      const selectedDecision =
        decision ?? (item.status === "Exception" ? "record_exception" : "request_evidence");
      const response = await apiCall(evidence.soc2PreflightReviewDecision, {
        params: { id: item.runId },
        body: {
          controlId: item.controlId,
          findingId: item.id,
          decision: selectedDecision,
          actor: "soc2-reviewer",
          rationale: rationale?.trim() || reviewDecisionRationale(item, selectedDecision),
          evidenceRefs: item.evidenceRefs,
        },
      });
      setReviewMessage(`Queue decision recorded · receipt ${response.receipt.id}`);
      const queue = await apiCall(evidence.soc2PreflightReviewQueue, {
        query: { limit: 6 },
      });
      setReviewQueue(queue);
      const remediationList = await apiCall(evidence.soc2PreflightRemediations, {
        query: { limit: 6 },
      });
      setRemediations(remediationList);
      if (reviewDetailItem?.id === item.id) {
        setReviewDetailItem(null);
        setReviewDetailRun(null);
        setReviewDecisionRationaleDraft("");
      }
      onImported();
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setReviewingId(null);
    }
  }

  async function recordFirstObservationCorrection(run: EvidenceSoc2PreflightRunSummaryDto) {
    setCorrectingId(run.id);
    setReviewError(null);
    setCorrectionMessage(null);
    try {
      const stored = await apiCall(evidence.soc2PreflightGet, {
        params: { id: run.id },
        query: {},
      });
      const control =
        stored.report.controls.find((row) => row.status !== "Verified" && row.observations.length > 0) ??
        stored.report.controls.find((row) => row.observations.length > 0);
      const observation = control?.observations[0];
      if (!control || !observation) {
        throw new Error("This run has no derived observation to correct.");
      }
      const response = await apiCall(evidence.soc2PreflightObservationCorrection, {
        params: { id: run.id },
        body: {
          observationId: observation.id,
          actor: "soc2-reviewer",
          correctedValue: `Reviewer correction: ${observation.value}`,
          rationale:
            "Reviewer corrected the derived observation without modifying the raw evidence artifact.",
        },
      });
      setCorrectionMessage(`Correction recorded · ${response.correction.id}`);
      onImported();
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setCorrectingId(null);
    }
  }

  function nextTemplateId(current: EvidenceSoc2TemplateIdDto | null): EvidenceSoc2TemplateIdDto {
    const ids: EvidenceSoc2TemplateIdDto[] = [
      "soc2-identity-mfa",
      "soc2-termination-access-removal",
      "soc2-periodic-access-review",
    ];
    const index = current ? ids.indexOf(current) : -1;
    return ids[(index + 1) % ids.length] ?? ids[0]!;
  }

  async function remapFirstControl(run: EvidenceSoc2PreflightRunSummaryDto) {
    setMappingId(run.id);
    setReviewError(null);
    setMappingMessage(null);
    try {
      const stored = await apiCall(evidence.soc2PreflightGet, {
        params: { id: run.id },
        query: {},
      });
      const control = stored.report.controls[0];
      if (!control) throw new Error("This run has no control to map.");
      const response = await apiCall(evidence.soc2PreflightTemplateMapping, {
        params: { id: run.id },
        body: {
          controlId: control.controlId,
          templateId: nextTemplateId(control.templateMapping.templateId),
          actor: "soc2-reviewer",
          rationale:
            "Reviewer tested reversible template remapping while preserving customer control language.",
        },
      });
      setMappingMessage(`Mapping updated · ${response.mapping.templateName}`);
      onImported();
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setMappingId(null);
    }
  }

  async function undoFirstControlMapping(run: EvidenceSoc2PreflightRunSummaryDto) {
    setMappingId(run.id);
    setReviewError(null);
    setMappingMessage(null);
    try {
      const stored = await apiCall(evidence.soc2PreflightGet, {
        params: { id: run.id },
        query: {},
      });
      const control = stored.report.controls[0];
      if (!control) throw new Error("This run has no control to unmap.");
      const response = await apiCall(evidence.soc2PreflightTemplateMapping, {
        params: { id: run.id },
        body: {
          controlId: control.controlId,
          templateId: null,
          actor: "soc2-reviewer",
          rationale:
            "Reviewer undid the template mapping; the original customer control text remains intact.",
        },
      });
      setMappingMessage(`Mapping undone · ${response.event.id}`);
      onImported();
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setMappingId(null);
    }
  }

  async function replayRun(run: EvidenceSoc2PreflightRunSummaryDto) {
    setReplayingId(run.id);
    setReplayMessage(null);
    setReviewError(null);
    try {
      const replay = await apiCall(evidence.soc2PreflightReplay, {
        params: { id: run.id },
        query: {},
      });
      setReplayMessage(
        replay.ok
          ? `Replay OK · ${replay.totalControls} controls · ${run.id}`
          : `Replay drift · ${replay.differences.length} difference${replay.differences.length === 1 ? "" : "s"} · ${run.id}`,
      );
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setReplayingId(null);
    }
  }

  async function loadScorecard(run: EvidenceSoc2PreflightRunSummaryDto) {
    setScorecardId(run.id);
    setScorecardError(null);
    try {
      const nextScorecard = await apiCall(evidence.soc2PreflightScorecard, {
        params: { id: run.id },
        query: {},
      });
      setScorecard(nextScorecard);
    } catch (e) {
      setScorecardError(e instanceof Error ? e.message : String(e));
    } finally {
      setScorecardId(null);
    }
  }

  async function loadEvidenceInventory(run: EvidenceSoc2PreflightRunSummaryDto) {
    setInventoryId(run.id);
    setInventoryError(null);
    try {
      const inventory = await apiCall(evidence.soc2PreflightEvidenceInventory, {
        params: { id: run.id },
        query: {},
      });
      setEvidenceInventory(inventory);
    } catch (e) {
      setInventoryError(e instanceof Error ? e.message : String(e));
    } finally {
      setInventoryId(null);
    }
  }

  async function loadSourceAlerts(run: EvidenceSoc2PreflightRunSummaryDto) {
    setSourceAlertsId(run.id);
    setSourceAlertsError(null);
    try {
      const alerts = await apiCall(evidence.soc2PreflightSourceAlerts, {
        params: { id: run.id },
        query: {},
      });
      setSourceAlerts(alerts);
    } catch (e) {
      setSourceAlertsError(e instanceof Error ? e.message : String(e));
    } finally {
      setSourceAlertsId(null);
    }
  }

  async function loadNotificationOutbox(run: EvidenceSoc2PreflightRunSummaryDto) {
    setNotificationOutboxId(run.id);
    setNotificationOutboxError(null);
    try {
      const outbox = await apiCall(evidence.soc2PreflightNotifications, {
        query: { runId: run.id, limit: 20 },
      });
      setNotificationOutbox(outbox);
    } catch (e) {
      setNotificationOutboxError(e instanceof Error ? e.message : String(e));
    } finally {
      setNotificationOutboxId(null);
    }
  }

  async function loadScheduleReport(run: EvidenceSoc2PreflightRunSummaryDto) {
    setScheduleReportId(run.id);
    setScheduleReportError(null);
    try {
      const report = await apiCall(evidence.soc2PreflightSchedules, {
        query: { runId: run.id, limit: 20 },
      });
      setScheduleReport(report);
    } catch (e) {
      setScheduleReportError(e instanceof Error ? e.message : String(e));
    } finally {
      setScheduleReportId(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle>SOC 2 evidence preflight</CardTitle>
            <CardDescription>
              {report.organization.name} · {report.auditPeriod.start} to {report.auditPeriod.end}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <IntentBadge intent="neutral">{report.policyVersion}</IntentBadge>
            <IntentBadge intent={summary.Exception > 0 ? "danger" : "success"}>
              {summary.Exception} exceptions
            </IntentBadge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 px-0">
        <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 xl:grid-cols-5">
          {[
            ["Coverage", pct(summary.verifiedCoverage), summary.Verified > 0 ? "success" : "warning"],
            ["Verified", String(summary.Verified), "success"],
            ["Exception", String(summary.Exception), summary.Exception > 0 ? "danger" : "neutral"],
            ["Needs determination", String(summary["Needs determination"]), summary["Needs determination"] > 0 ? "warning" : "neutral"],
            ["Stale/not tested", String(summary["Stale/not tested"]), summary["Stale/not tested"] > 0 ? "warning" : "neutral"],
          ].map(([label, value, intent]) => (
            <div key={label} className="bg-card px-4 py-3">
              <div className="type-subtext text-muted-foreground">{label}</div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <span className={`${NUM} type-heading font-medium`}>{value}</span>
                <IntentBadge intent={intent as Intent}>{label === "Coverage" ? "scope" : "controls"}</IntentBadge>
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(20rem,0.75fr)]">
          <div className="space-y-3">
            <div className="type-body font-medium">Controls</div>
            <DataTable
              rows={report.controls}
              columns={controlColumns}
              rowKey={(row) => row.controlId}
              wrapInCard={false}
            />
            {selectedControl ? (
              <div className="rounded-lg border border-border bg-background p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="type-subtext text-muted-foreground">{selectedControl.controlId}</code>
                      <IntentBadge intent={soc2StatusIntent(selectedControl.status)}>
                        {selectedControl.status}
                      </IntentBadge>
                      <IntentBadge intent="neutral">{selectedControl.policyVersion}</IntentBadge>
                    </div>
                    <div className="type-body font-medium">{selectedControl.title}</div>
                    <p className="type-body text-muted-foreground">{selectedControl.controlText}</p>
                  </div>
                  <div className="type-subtext text-muted-foreground">
                    {selectedControl.owner} · {selectedControl.tsc} · {selectedControl.family}
                  </div>
                </div>
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <div className="space-y-2">
                    <div className="type-subtext font-medium uppercase text-muted-foreground">Requirements</div>
                    <div className="space-y-2">
                      {selectedControl.requirements.map((requirement) => (
                        <div key={requirement.id} className="rounded-md bg-muted/40 px-3 py-2">
                          <code className="type-subtext text-muted-foreground">{requirement.id}</code>
                          <div className="mt-1 type-body">{requirement.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="type-subtext font-medium uppercase text-muted-foreground">Evidence lineage</div>
                    {selectedControl.evidenceArtifacts.length > 0 ? (
                      <div className="space-y-2">
                        {selectedControl.evidenceArtifacts.map((artifact) => (
                          <div key={artifact.id} className="rounded-md bg-muted/40 px-3 py-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <code className="type-subtext text-muted-foreground">{artifact.id}</code>
                              <IntentBadge intent="neutral">{artifact.type}</IntentBadge>
                            </div>
                            <div className="mt-1 type-body font-medium">{artifact.title}</div>
                            <div className="mt-1 type-subtext text-muted-foreground">
                              {artifact.source} · {artifact.collectedAt} · {artifact.actor}
                            </div>
                            <div className="mt-1 truncate type-subtext text-muted-foreground" title={artifact.contentHash}>
                              {artifact.contentHash}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                        No evidence artifacts are linked to this control.
                      </div>
                    )}
                  </div>
                </div>
	                <div className="mt-4 rounded-md border border-border bg-muted/30 px-3 py-3">
	                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="type-subtext font-medium uppercase text-muted-foreground">Policy checks</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <IntentBadge intent={soc2StatusIntent(selectedControl.policyEvaluation.verdict)}>
                          {selectedControl.policyEvaluation.verdict}
                        </IntentBadge>
                        <IntentBadge intent="neutral">
                          {selectedControl.policyEvaluation.checkRuns.length} checks
                        </IntentBadge>
                      </div>
                    </div>
                    <div className="min-w-0 text-left type-subtext text-muted-foreground sm:text-right">
                      <div>{selectedControl.policyEvaluation.policy.version}</div>
                      <div
                        className="mt-1 truncate font-mono"
                        title={selectedControl.policyEvaluation.policy.canonicalHash}
                      >
                        {selectedControl.policyEvaluation.policy.canonicalHash}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2">
                    {selectedControl.policyEvaluation.checkRuns.map((checkRun) => (
                      <div
                        key={`${checkRun.policyId}:${checkRun.checkId}`}
                        className="rounded-md bg-background px-3 py-2"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0">
                            <code className="type-subtext text-muted-foreground">{checkRun.checkId}</code>
                            <div className="mt-1 type-subtext text-muted-foreground">
                              {checkRun.requirementId} · {checkRun.kind.replace(/_/g, " ")}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <IntentBadge intent={soc2CheckResultIntent(checkRun.result)}>
                              {checkRun.result.replace(/_/g, " ")}
                            </IntentBadge>
                            <IntentBadge intent={checkRun.consequence === "critical" ? "danger" : "neutral"}>
                              {checkRun.consequence}
                            </IntentBadge>
                          </div>
                        </div>
                        <div className="mt-2 type-body">{checkRun.reason}</div>
                        <div className="mt-1 type-subtext text-muted-foreground">
                          evidence {checkRun.evidenceRefs.length > 0 ? checkRun.evidenceRefs.join(", ") : "none"} ·
                          observations{" "}
                          {checkRun.observationIds.length > 0 ? checkRun.observationIds.join(", ") : "none"} ·{" "}
                          {checkRun.engineVersion}
                        </div>
                      </div>
	                    ))}
	                  </div>
	                </div>
	                <div
	                  className="mt-4 rounded-md border border-border bg-background px-3 py-3"
	                  aria-labelledby="soc2-policy-studio-preview-title"
	                >
	                  <div className="flex flex-wrap items-start justify-between gap-2">
	                    <div className="min-w-0">
	                      <div id="soc2-policy-studio-preview-title" className="type-body font-medium">
	                        Policy Studio admission preview
	                      </div>
	                      <div className="mt-1 type-subtext text-muted-foreground">
	                        Human-approved policy proposal view before activation, loosening, or semantic-worker use.
	                      </div>
	                    </div>
	                    <div className="flex flex-wrap gap-2">
	                      <IntentBadge intent={selectedPolicyWorkflow ? "success" : "warning"}>
	                        route-fed workflow
	                      </IntentBadge>
	                      <IntentBadge intent="neutral">proposal only</IntentBadge>
	                      <IntentBadge intent="neutral">
	                        {selectedPolicyWorkflow?.summary.totalProposals ?? 0} proposals
	                      </IntentBadge>
	                    </div>
	                  </div>
	                  <div className="mt-3 grid gap-2 lg:grid-cols-5">
	                    {selectedPolicyAdmissionSignals.map((signal) => (
	                      <div key={signal.label} className="rounded-md bg-muted/40 px-3 py-2">
	                        <div className="flex items-center justify-between gap-2">
	                          <div className="truncate type-subtext text-muted-foreground">{signal.label}</div>
	                          <IntentBadge intent={signal.intent as Intent}>guard</IntentBadge>
	                        </div>
	                        <div className={`${NUM} mt-1 truncate type-body font-medium`}>{signal.value}</div>
	                        <div className="mt-1 line-clamp-3 type-subtext text-muted-foreground">{signal.detail}</div>
	                      </div>
	                    ))}
	                  </div>
	                  {selectedPolicyWorkflow && selectedPolicyStudioState ? (
	                    <div className="mt-3 grid gap-3 lg:grid-cols-[1.1fr_0.9fr]">
	                      <div className="rounded-md bg-muted/40 px-3 py-2">
	                        <div className="flex flex-wrap items-center justify-between gap-2">
	                          <div className="type-subtext font-medium uppercase text-muted-foreground">
	                            Proposal workflow
	                          </div>
	                          <div className="flex flex-wrap gap-2">
	                            <IntentBadge intent="neutral">
	                              {selectedPolicyWorkflow.summary.awaitingActivation} awaiting activation
	                            </IntentBadge>
	                            <IntentBadge intent="danger">
	                              {selectedPolicyWorkflow.summary.blocked} blocked
	                            </IntentBadge>
	                            <IntentBadge intent="success">
	                              {selectedPolicyWorkflow.summary.activated} activated
	                            </IntentBadge>
	                          </div>
	                        </div>
	                        <div className="mt-2 grid gap-2">
	                          {selectedPolicyWorkflow.proposals.map((proposal) => (
	                            <div key={proposal.proposalId} className="rounded-md bg-background px-3 py-2">
	                              <div className="flex flex-wrap items-center justify-between gap-2">
	                                <code className="truncate type-subtext text-muted-foreground">{proposal.proposalId}</code>
	                                <IntentBadge intent={soc2PolicyProposalIntent(proposal.status)}>
	                                  {proposal.status.replace(/_/g, " ")}
	                                </IntentBadge>
	                              </div>
	                              <div className="mt-1 type-body">
	                                {proposal.previousPolicyId} {"->"} {proposal.proposedPolicyId}
	                              </div>
	                              <div className="mt-1 type-subtext text-muted-foreground">
	                                {proposal.activationIntent} activation · {proposal.impacts.length} frozen-case impact rows ·
	                                fingerprint {proposal.impactFingerprint.slice(0, 18)}
	                              </div>
	                              {proposal.blockedReasons.length > 0 ? (
	                                <div className="mt-2 flex flex-wrap gap-1">
	                                  {proposal.blockedReasons.slice(0, 4).map((reason) => (
	                                    <IntentBadge key={reason} intent="warning">{reason}</IntentBadge>
	                                  ))}
	                                </div>
	                              ) : null}
	                            </div>
	                          ))}
	                        </div>
	                      </div>
	                      <div className="rounded-md bg-muted/40 px-3 py-2">
	                        <div className="flex flex-wrap items-center justify-between gap-2">
	                          <div className="type-subtext font-medium uppercase text-muted-foreground">Route actions</div>
	                          <IntentBadge intent="neutral">{selectedPolicyStudioState.actorRole}</IntentBadge>
	                        </div>
	                        <div className="mt-2 grid gap-2">
	                          {selectedPolicyActions.map((action) => (
	                            <div key={`${action.proposalId}:${action.action}`} className="rounded-md bg-background px-3 py-2">
	                              <div className="flex flex-wrap items-center justify-between gap-2">
	                                <span className="type-body font-medium">{action.action.replace(/_/g, " ")}</span>
	                                <IntentBadge intent={soc2PolicyActionIntent(action.ok)}>{action.next}</IntentBadge>
	                              </div>
	                              <div className="mt-1 line-clamp-2 type-subtext text-muted-foreground">
	                                {action.blockers.length > 0
	                                  ? action.blockers.join(", ")
	                                  : "No blockers for this route action."}
	                              </div>
	                            </div>
	                          ))}
	                        </div>
	                      </div>
	                    </div>
	                  ) : (
	                    <div className="mt-3 rounded-md bg-muted/40 px-3 py-2 type-subtext text-muted-foreground">
	                      Route workflow state has not loaded yet; activation remains unavailable without the server contract.
	                    </div>
	                  )}
	                  <div className="mt-3 type-subtext text-muted-foreground">
	                    This route-fed preview keeps policy proposal, unknown routing, impact analysis, activation-idempotency-key-required,
	                    activation-impact-fingerprint-required, and authorized activation visible without adding a silent policy write path.
	                  </div>
	                </div>
	                <div className="mt-4 rounded-md border border-border bg-muted/30 px-3 py-3">
	                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="type-subtext font-medium uppercase text-muted-foreground">Template mapping</div>
                      <div className="mt-1 type-body font-medium">
                        {selectedControl.templateMapping.templateName}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent={selectedControl.templateMapping.status === "mapped" ? "success" : "warning"}>
                        {selectedControl.templateMapping.status}
                      </IntentBadge>
                      <IntentBadge intent="neutral">
                        {pct(selectedControl.templateMapping.confidence)} confidence
                      </IntentBadge>
                    </div>
                  </div>
                  <div className="mt-2 type-subtext text-muted-foreground">
                    {selectedControl.templateMapping.mapper} · reversible · customer text preserved
                  </div>
                  <div className="mt-3 grid gap-3 lg:grid-cols-2">
                    <div>
                      <div className="type-subtext font-medium text-muted-foreground">Source expectations</div>
                      <ul className="mt-1 space-y-1 type-subtext text-muted-foreground">
                        {selectedControl.templateMapping.sourceExpectations.map((item) => (
                          <li key={item}>- {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="type-subtext font-medium text-muted-foreground">Unknown routes</div>
                      <ul className="mt-1 space-y-1 type-subtext text-muted-foreground">
                        {selectedControl.templateMapping.unknownRoutes.map((item) => (
                          <li key={item}>- {item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="type-subtext font-medium uppercase text-muted-foreground">Population reconciliation</div>
                  {selectedControl.populationReconciliations.length > 0 ? (
                    <div className="grid gap-2 lg:grid-cols-2">
                      {selectedControl.populationReconciliations.map((reconciliation) => (
                        <div key={reconciliation.id} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <code className="type-subtext text-muted-foreground">{reconciliation.id}</code>
                              <div className="mt-1 type-body font-medium">{reconciliation.population}</div>
                            </div>
                            <IntentBadge intent={soc2StatusIntent(reconciliation.status)}>
                              {reconciliation.status}
                            </IntentBadge>
                          </div>
                          <div className="mt-2 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-3">
                            {[
                              ["Coverage", pct(reconciliation.coverage)],
                              ["Matched", String(reconciliation.matchedCount)],
                              ["Expected", String(reconciliation.authoritativeCount)],
                            ].map(([label, value]) => (
                              <div key={label} className="bg-background px-2 py-1.5">
                                <div className="type-subtext text-muted-foreground">{label}</div>
                                <div className={`${NUM} type-subtext font-medium`}>{value}</div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-2 type-body">{reconciliation.reason}</div>
                          <div className="mt-1 type-subtext text-muted-foreground">
                            {reconciliation.authoritativeSource} {"->"} {reconciliation.testedSource}
                          </div>
                          {reconciliation.missingSubjects.length > 0 ? (
                            <div className="mt-1 truncate type-subtext text-muted-foreground" title={reconciliation.missingSubjects.join(", ")}>
                              missing {reconciliation.missingSubjects.join(", ")}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                      No structured population reconciliation was supplied for this control.
                    </div>
                  )}
                </div>
                <div className="mt-4 space-y-2">
                  <div className="type-subtext font-medium uppercase text-muted-foreground">Derived observations</div>
                  {selectedControl.observations.length > 0 ? (
                    <div className="grid gap-2 lg:grid-cols-2">
                      {selectedControl.observations.map((observation) => (
                        <div key={observation.id} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <code className="type-subtext text-muted-foreground">{observation.checkId}</code>
                            <IntentBadge intent="neutral">{observation.type.replace(/_/g, " ")}</IntentBadge>
                          </div>
                          <div className="mt-1 type-body">{observation.value}</div>
                          <div className="mt-1 type-subtext text-muted-foreground">
                            {observation.provenance.source} · {observation.extractorVersion} · confidence{" "}
                            {pct(observation.parseConfidence)}
                          </div>
                          <div className="mt-1 truncate type-subtext text-muted-foreground" title={observation.id}>
                            {observation.id}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                      No typed observations were derived from this control's evidence.
                    </div>
                  )}
                </div>
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <div className="space-y-2">
                    <div className="type-subtext font-medium uppercase text-muted-foreground">Findings</div>
                    {selectedControl.findings.length > 0 ? (
                      <div className="space-y-2">
                        {selectedControl.findings.map((finding) => (
                          <div
                            key={`${finding.controlId}:${finding.requirementId ?? "control"}:${finding.checkId ?? finding.reason}`}
                            className="rounded-md bg-muted/40 px-3 py-2"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <IntentBadge intent={soc2StatusIntent(finding.status)}>{finding.status}</IntentBadge>
                              <IntentBadge intent={finding.severity === "critical" ? "danger" : "warning"}>
                                {finding.severity}
                              </IntentBadge>
                              <code className="type-subtext text-muted-foreground">
                                {finding.requirementId ?? "control-level"}
                              </code>
                            </div>
                            <div className="mt-2 type-body">{finding.reason}</div>
                            <div className="mt-1 type-subtext text-muted-foreground">{finding.action}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-md bg-muted/40 px-3 py-2 type-subtext text-muted-foreground">
                        No findings. Required checks passed for this policy version.
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="type-subtext font-medium uppercase text-muted-foreground">Receipt history</div>
                    <div className="space-y-2">
                      {selectedReceiptHistory.map((receipt) => (
                        <div key={receipt.id} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <IntentBadge intent={soc2StatusIntent(receipt.status)}>{receipt.status}</IntentBadge>
                            <code className="type-subtext text-muted-foreground">{receipt.id}</code>
                          </div>
                          <div className="mt-1 type-subtext text-muted-foreground">
                            issued {receipt.issuedAt} · {receipt.engineVersion}
                          </div>
                          <div className="mt-1 truncate type-subtext text-muted-foreground" title={receipt.canonicalHash}>
                            {receipt.canonicalHash}
                          </div>
                          <div className="mt-1 truncate type-subtext text-muted-foreground" title={receipt.policyHash}>
                            policy {receipt.policyHash}
                          </div>
                          {receipt.parentReceiptId ? (
                            <div className="mt-1 truncate type-subtext text-muted-foreground">
                              parent {receipt.parentReceiptId}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div className="space-y-3">
            <div className="type-body font-medium">Verification inbox</div>
            {findings.length > 0 ? (
              <DataTable
                rows={findings}
                columns={findingColumns}
                rowKey={(row) => `${row.controlId}:${row.checkId ?? row.requirementId ?? row.status}`}
                wrapInCard={false}
              />
            ) : (
              <div className="rounded-lg border border-dashed border-border p-4 type-body text-muted-foreground">
                No actionable exceptions or uncertain controls in this package.
              </div>
            )}
            <div className="rounded-lg border border-border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="type-body font-medium">Saved runs</div>
                  <div className="type-subtext text-muted-foreground">
                    {runs.length} persisted preflight package{runs.length === 1 ? "" : "s"}
                  </div>
                </div>
                    <Button
                      size="sm"
                      onClick={() => void saveRepresentativeRun()}
                      disabled={saving}
                      aria-label="Save representative SOC 2 preflight package"
                    >
                      {saving ? "Saving..." : "Save package"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void batchRepresentativeRun()}
                      disabled={batching}
                      aria-label="Run representative SOC 2 preflight package as an idempotent batch"
                    >
                      <Icon name="play" size="sm" />
                      {batching ? "Running..." : "Batch run"}
                    </Button>
              </div>
              <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                <div className="mb-4 rounded-md bg-muted/40 px-3 py-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="type-body font-medium">Persistent onboarding state</div>
                      <div className="type-subtext text-muted-foreground">
                        Import jobs, mapping drafts, and first-run instrumentation stay visible after preview.
                      </div>
                    </div>
                    <IntentBadge
                      intent={
                        importPersistenceNextAction === "fix-preview-blockers"
                          ? "danger"
                          : importPersistenceNextAction === "run-first-verification" ||
                              importPersistenceNextAction === "review-first-finding"
                            ? "success"
                            : "warning"
                      }
                    >
                      {importPersistenceNextAction}
                    </IntentBadge>
                  </div>
                  <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-4">
                    {[
                      ["Persisted import jobs", String(persistedImportJobCount)],
                      ["Mapping drafts", String(persistedMappingDraftCount)],
                      ["Ready import jobs", String(persistedReadyJobCount)],
                      ["Time-to-first-finding", timeToFirstFindingLabel],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-background px-3 py-2">
                        <div className="type-subtext text-muted-foreground">{label}</div>
                        <div className={`${NUM} mt-1 type-body font-medium`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {persistedImportJobIds.length > 0 ? (
                    <div className="mt-3 grid gap-2">
                      {persistedImportJobIds.map((jobId) => {
                        const run = runs.find((row) => row.id === jobId) ?? null;
                        const ready = importOnboardingState?.readyJobIds.includes(jobId) ?? false;
                        return (
                        <div
                          key={jobId}
                          className="flex flex-col gap-1 rounded-md bg-muted/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0">
                            <div className="truncate type-body font-medium">
                              {run?.label ?? jobId}
                            </div>
                            <div className="truncate type-subtext text-muted-foreground">
                              {run
                                ? `${run.auditPeriodId} · ${run.organizationName}`
                                : `persisted job · ${jobId}`}
                            </div>
                          </div>
                          <IntentBadge intent={ready ? "success" : "warning"}>
                            {ready ? "ready" : "persisted"}
                          </IntentBadge>
                        </div>
                        );
                      })}
                      {latestMappingDraftId ? (
                        <div className="rounded-md bg-muted/40 px-3 py-2 type-subtext text-muted-foreground">
                          Latest mapping draft: <span className={NUM}>{latestMappingDraftId}</span>
                        </div>
                      ) : null}
                      {importOnboardingBlockedReasons.length > 0 ? (
                        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 type-subtext text-destructive">
                          {importOnboardingBlockedReasons.join(", ")}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                      No persisted import jobs yet. Save a validated import package to create the first durable onboarding record.
                    </div>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid min-w-0 gap-1 type-subtext text-muted-foreground">
                    <span>Organization name</span>
                    <input
                      className={CANDIDATE_FIELD}
                      value={importOrganizationName}
                      onChange={(event) => setImportOrganizationName(event.currentTarget.value)}
                    />
                  </label>
                  <label className="grid min-w-0 gap-1 type-subtext text-muted-foreground">
                    <span>Organization ID</span>
                    <input
                      className={CANDIDATE_FIELD}
                      value={importOrganizationId}
                      onChange={(event) => setImportOrganizationId(event.currentTarget.value)}
                    />
                  </label>
                  <label className="grid min-w-0 gap-1 type-subtext text-muted-foreground">
                    <span>Audit period</span>
                    <input
                      className={CANDIDATE_FIELD}
                      value={importAuditPeriodId}
                      onChange={(event) => setImportAuditPeriodId(event.currentTarget.value)}
                    />
                  </label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="grid min-w-0 gap-1 type-subtext text-muted-foreground">
                      <span>Start</span>
                      <input
                        type="date"
                        className={CANDIDATE_FIELD}
                        value={importAuditPeriodStart}
                        onChange={(event) => setImportAuditPeriodStart(event.currentTarget.value)}
                      />
                    </label>
                    <label className="grid min-w-0 gap-1 type-subtext text-muted-foreground">
                      <span>End</span>
                      <input
                        type="date"
                        className={CANDIDATE_FIELD}
                        value={importAuditPeriodEnd}
                        onChange={(event) => setImportAuditPeriodEnd(event.currentTarget.value)}
                      />
                    </label>
                  </div>
                </div>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
                  <label className="grid min-w-0 flex-1 gap-1 type-subtext text-muted-foreground">
                    <span>Import package label</span>
                    <input
                      className={CANDIDATE_FIELD}
                      value={importLabel}
                      onChange={(event) => setImportLabel(event.currentTarget.value)}
                      placeholder="Q3 control matrix"
                    />
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" asChild>
                      <label>
                        <Icon name="upload" size="sm" />
                        Preview file
                        <input
                          type="file"
                          accept="application/json,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.json,.csv,.xlsx"
                          className="sr-only"
                          aria-label="Choose SOC 2 JSON, CSV, or XLSX package to preview"
                          onChange={(event) => void previewImportPackage(event)}
                        />
                      </label>
                    </Button>
	                    <Button
	                      size="sm"
	                      onClick={() => void saveImportedPackage()}
	                      disabled={
	                        !importPackage ||
	                        importing ||
	                        importIssues.errors.length > 0 ||
	                        ((importKind === "csv" || importKind === "xlsx") && !importAuditContextComplete)
	                      }
	                      aria-label="Save the previewed SOC 2 import package"
	                    >
                      {importing ? "Saving..." : "Save import"}
                    </Button>
                  </div>
                </div>
                {importIssues.errors.length > 0 || importIssues.warnings.length > 0 ? (
                  <div className="mt-3 space-y-1">
                    {[...importIssues.errors, ...importIssues.warnings].slice(0, 4).map((issue) => (
                      <div
                        key={`${issue.severity}:${issue.row}:${issue.field}:${issue.message}`}
                        className="flex items-start gap-2 type-subtext text-muted-foreground"
                      >
                        <IntentBadge intent={issue.severity === "error" ? "danger" : "warning"}>
                          {issue.severity}
                        </IntentBadge>
                        <span>
                          row {issue.row} · {issue.field}: {issue.message}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
	                {importPreview ? (
	                  <div className="mt-3 space-y-3">
	                    <div className="flex flex-wrap items-center gap-2">
	                      <IntentBadge intent={importPreviewReady ? "success" : "danger"}>
	                        {importPreviewReady ? "ready to save" : "needs fixes"}
	                      </IntentBadge>
	                      <IntentBadge intent="neutral">{importKind ?? "json"} preview</IntentBadge>
	                      <span className="type-subtext text-muted-foreground">
	                        {importPreviewTemplateMapped}/{importPreview.summary.totalControls} controls mapped ·{" "}
	                        {importPreviewFamilies.length} families
	                      </span>
	                    </div>
	                    <div className="grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-4">
	                      {[
	                        ["Controls", String(importPreview.summary.totalControls)],
	                        ["Verified", String(importPreview.summary.Verified)],
	                        ["Exceptions", String(importPreview.summary.Exception)],
	                        ["Needs determination", String(importPreview.summary["Needs determination"])],
	                      ].map(([label, value]) => (
	                        <div key={label} className="bg-card px-3 py-2">
	                          <div className="type-subtext text-muted-foreground">{label}</div>
	                          <div className={`${NUM} type-body font-medium`}>{value}</div>
	                        </div>
	                      ))}
	                    </div>
	                    <div className="rounded-md bg-muted/40 px-3 py-2">
	                      <div className="flex flex-wrap gap-2">
	                        {importPreviewFamilies.map((family) => (
	                          <IntentBadge key={family} intent="neutral">
	                            {family}
	                          </IntentBadge>
	                        ))}
	                      </div>
	                      {importPreviewPrimaryFinding ? (
	                        <div className="mt-2 type-subtext text-muted-foreground">
	                          <span className="font-medium text-foreground">First finding:</span>{" "}
	                          {importPreviewPrimaryFinding.controlId} · {importPreviewPrimaryFinding.reason}
	                        </div>
	                      ) : (
	                        <div className="mt-2 type-subtext text-muted-foreground">
	                          No actionable findings in this preview.
	                        </div>
	                      )}
	                    </div>
	                  </div>
	                ) : null}
              </div>
              <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <label className="grid min-w-0 flex-1 gap-1 type-subtext text-muted-foreground">
                    <span>Export evidence refs</span>
                    <input
                      className={CANDIDATE_FIELD}
                      value={exportEvidenceRefsDraft}
                      onChange={(event) => setExportEvidenceRefsDraft(event.currentTarget.value)}
                      placeholder="all evidence, or ev-ref-1, ev-ref-2"
                    />
                  </label>
                  <label className="flex min-h-9 items-center gap-2 type-subtext text-muted-foreground">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border"
                      checked={exportIncludeExpiredEvidence}
                      onChange={(event) => setExportIncludeExpiredEvidence(event.currentTarget.checked)}
                    />
                    <span>Include expired evidence</span>
                  </label>
                </div>
                <div className="mt-2 type-subtext text-muted-foreground">
                  Leave evidence refs blank for the full packet. Enter refs to create a permission-scoped export that redacts excluded evidence.
                </div>
              </div>
              {saveError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Save failed</AlertTitle>
                  <AlertDescription>{saveError}</AlertDescription>
                </Alert>
              ) : null}
              {batchError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Batch failed</AlertTitle>
                  <AlertDescription>{batchError}</AlertDescription>
                </Alert>
              ) : null}
              {importError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Import failed</AlertTitle>
                  <AlertDescription>{importError}</AlertDescription>
                </Alert>
              ) : null}
              {reviewError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Review failed</AlertTitle>
                  <AlertDescription>{reviewError}</AlertDescription>
                </Alert>
              ) : null}
              {reviewDetailError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Review detail failed</AlertTitle>
                  <AlertDescription>{reviewDetailError}</AlertDescription>
                </Alert>
              ) : null}
              {exportError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Export failed</AlertTitle>
                  <AlertDescription>{exportError}</AlertDescription>
                </Alert>
              ) : null}
              {scorecardError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Scorecard failed</AlertTitle>
                  <AlertDescription>{scorecardError}</AlertDescription>
                </Alert>
              ) : null}
              {inventoryError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Inventory failed</AlertTitle>
                  <AlertDescription>{inventoryError}</AlertDescription>
                </Alert>
              ) : null}
              {sourceAlertsError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Source alerts failed</AlertTitle>
                  <AlertDescription>{sourceAlertsError}</AlertDescription>
                </Alert>
              ) : null}
              {notificationOutboxError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Notification outbox failed</AlertTitle>
                  <AlertDescription>{notificationOutboxError}</AlertDescription>
                </Alert>
              ) : null}
              {scheduleReportError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Schedule report failed</AlertTitle>
                  <AlertDescription>{scheduleReportError}</AlertDescription>
                </Alert>
              ) : null}
              {auditPacketError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Audit packet failed</AlertTitle>
                  <AlertDescription>{auditPacketError}</AlertDescription>
                </Alert>
              ) : null}
              {savedId ? (
                <div className="mt-3 type-subtext text-muted-foreground">
                  Saved run <code>{savedId}</code>
                </div>
              ) : null}
              {batchMessage ? (
                <div className="mt-3 type-subtext text-muted-foreground">{batchMessage}</div>
              ) : null}
              {visibleBatchJob ? (
                <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="type-body font-medium">Batch progress</div>
                      <div className="type-subtext text-muted-foreground">
                        {visibleBatchJob.id} · attempt {visibleBatchJob.attempts}
                      </div>
                    </div>
                    <IntentBadge
                      intent={
                        visibleBatchJob.status === "completed"
                          ? "success"
                          : visibleBatchJob.status === "failed"
                            ? "danger"
                            : "warning"
                      }
                    >
                      {visibleBatchJob.status}
                    </IntentBadge>
                  </div>
                  <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-3">
                    {[
                      ["Completed", `${visibleBatchJob.progress.completedControls}/${visibleBatchJob.progress.totalControls}`],
                      ["Failed", String(visibleBatchJob.progress.failedControls)],
                      ["Run", visibleBatchJob.runId ?? "pending"],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-card px-3 py-2">
                        <div className="type-subtext text-muted-foreground">{label}</div>
                        <div className={`${NUM} truncate type-body font-medium`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 type-subtext text-muted-foreground">
                    Idempotency key {visibleBatchJob.idempotencyKey}
                  </div>
                </div>
              ) : null}
              {reviewMessage || correctionMessage || mappingMessage || replayMessage ? (
                <div className="mt-3 space-y-1 type-subtext text-muted-foreground" role="status" aria-live="polite">
                  {reviewMessage ? <div>{reviewMessage}</div> : null}
                  {correctionMessage ? <div>{correctionMessage}</div> : null}
                  {mappingMessage ? <div>{mappingMessage}</div> : null}
                  {replayMessage ? <div>{replayMessage}</div> : null}
                </div>
              ) : null}
              {queueError ? (
                <Alert variant="destructive" className="mt-3">
                  <AlertTitle>Verification inbox failed</AlertTitle>
                  <AlertDescription>{queueError}</AlertDescription>
                </Alert>
              ) : null}
              {reviewQueue ? (
                <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="type-body font-medium">Run verification inbox</div>
                      <div className="type-subtext text-muted-foreground">
                        {reviewQueue.summary.totalActionable} actionable · {reviewQueue.summary.reviewedFiltered} reviewed
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent={reviewQueue.summary.exceptions > 0 ? "danger" : "success"}>
                        {reviewQueue.summary.exceptions} exceptions
                      </IntentBadge>
                      <IntentBadge intent={reviewQueue.summary.needsDetermination > 0 ? "warning" : "success"}>
                        {reviewQueue.summary.needsDetermination} needs determination
                      </IntentBadge>
                    </div>
                  </div>
                  {reviewQueue.items.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {reviewQueue.items.slice(0, 4).map((item) => (
                        <div key={item.id} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <IntentBadge intent={soc2StatusIntent(item.status)}>
                                  {item.status}
                                </IntentBadge>
                                <code className="type-subtext text-muted-foreground">{item.controlId}</code>
                              </div>
                              <div className="mt-1 truncate type-body font-medium">{item.controlTitle}</div>
                              <div className="mt-1 line-clamp-2 type-subtext text-muted-foreground" title={item.reason}>
                                {item.reason}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => void loadReviewDetail(item)}
                                disabled={reviewDetailId === item.id}
                                aria-label={`Inspect SOC 2 review detail for ${item.controlId}`}
                              >
                                <Icon name="search" size="sm" />
                                {reviewDetailId === item.id ? "Opening..." : "Inspect"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => void recordQueueReviewDecision(item)}
                                disabled={reviewingId === item.runId}
                                aria-label={`Record default review decision for ${item.controlId}`}
                              >
                                <Icon name="check" size="sm" />
                                {reviewingId === item.runId ? "Recording..." : "Review"}
                              </Button>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 type-subtext text-muted-foreground">
                            <span>{item.owner}</span>
                            <span>{item.auditPeriodId}</span>
                            <span>{item.severity}</span>
                            <span>{item.evidenceRefs.length} evidence refs</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                      No unresolved failed or uncertain SOC 2 findings.
                    </div>
                  )}
                </div>
              ) : null}
              {semanticWorkerReview ? (
                <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="type-body font-medium">Semantic worker orchestration</div>
                      <div className="type-subtext text-muted-foreground">
                        {semanticWorkerReview.summary.total} queue rows · {semanticWorkerQueueLabel}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent={semanticWorkerBlocking > 0 ? "danger" : "success"}>
                        {semanticWorkerBlocking} blocking
                      </IntentBadge>
                      <IntentBadge intent={semanticWorkerReview.persistedQueueBacked ? "success" : "warning"}>
                        {semanticWorkerReview.persistedQueueBacked ? "persisted" : "not persisted"}
                      </IntentBadge>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-4">
                    {semanticWorkerReviewSignals.map(([label, value, intent]) => (
                      <div key={label} className="bg-card px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="type-subtext text-muted-foreground">{label}</div>
                          <IntentBadge intent={intent as Intent}>worker</IntentBadge>
                        </div>
                        <div className={`${NUM} mt-1 truncate type-body font-medium`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {semanticWorkerRowsVisible.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {semanticWorkerRowsVisible.slice(0, 4).map((row) => (
                        <div key={row.dispatchId} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <IntentBadge
                                  intent={
                                    row.severity === "blocking"
                                      ? "danger"
                                      : row.severity === "warning"
                                        ? "warning"
                                        : "success"
                                  }
                                >
                                  {row.status.replace(/_/g, " ")}
                                </IntentBadge>
                                <code className="type-subtext text-muted-foreground">{row.controlId}</code>
                              </div>
                              <div className="mt-1 truncate type-body font-medium">{row.controlTitle}</div>
                              <div className="mt-1 line-clamp-2 type-subtext text-muted-foreground" title={row.reason}>
                                {row.reason}
                              </div>
                            </div>
                            <div className="min-w-[8rem] text-right type-subtext text-muted-foreground">
                              <div>{row.action.replace(/_/g, " ")}</div>
                              <div>
                                {row.workerId ?? row.leaseOwnerId ?? "no lease owner"}
                              </div>
                              <div>{row.workerHealthReason ?? row.workerStatus ?? "health pending"}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                      No semantic-worker rows are visible for the selected run.
                    </div>
                  )}
                </div>
              ) : null}
              {reviewDetailItem ? (
                <section
                  className="mt-3 rounded-md border border-border bg-background px-3 py-3"
                  aria-labelledby={`soc2-review-detail-${reviewDetailItem.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div id={`soc2-review-detail-${reviewDetailItem.id}`} className="type-body font-medium">
                        Review detail
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <IntentBadge intent={soc2StatusIntent(reviewDetailItem.status)}>
                          {reviewDetailItem.status}
                        </IntentBadge>
                        <IntentBadge intent={reviewDetailItem.severity === "critical" ? "danger" : "warning"}>
                          {reviewDetailItem.severity}
                        </IntentBadge>
                        <code className="type-subtext text-muted-foreground">{reviewDetailItem.controlId}</code>
                      </div>
                    </div>
                    <div className="type-subtext text-muted-foreground">
                      {reviewDetailItem.owner} · {reviewDetailItem.auditPeriodId}
                    </div>
                  </div>
                  <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)]">
                    <div className="space-y-2">
                      <div className="rounded-md bg-muted/40 px-3 py-2">
                        <div className="type-subtext font-medium text-muted-foreground">Control</div>
                        <div className="mt-1 type-body font-medium">{reviewDetailItem.controlTitle}</div>
                        <div className="mt-1 line-clamp-3 type-subtext text-muted-foreground" title={reviewDetailItem.controlText}>
                          {reviewDetailItem.controlText}
                        </div>
                      </div>
                      <div className="rounded-md bg-muted/40 px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            id={`soc2-review-condition-${reviewDetailItem.id}`}
                            className="type-subtext font-medium text-muted-foreground"
                          >
                            Failed or uncertain condition
                          </span>
                          <code className="type-subtext text-muted-foreground">
                            {reviewDetailItem.requirementId ?? "control-level"}
                          </code>
                          {reviewDetailItem.checkId ? (
                            <code className="type-subtext text-muted-foreground">{reviewDetailItem.checkId}</code>
                          ) : null}
                        </div>
                        <div id={`soc2-review-reason-${reviewDetailItem.id}`} className="mt-2 type-body">
                          {reviewDetailItem.reason}
                        </div>
                        <div id={`soc2-review-action-${reviewDetailItem.id}`} className="mt-1 type-subtext text-muted-foreground">
                          {reviewDetailItem.action}
                        </div>
                      </div>
                      {reviewDetailCheck ? (
                        <div className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <div className="type-subtext font-medium text-muted-foreground">Policy check</div>
                              <code className="type-subtext text-muted-foreground">{reviewDetailCheck.kind.replace(/_/g, " ")}</code>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <IntentBadge intent={soc2CheckResultIntent(reviewDetailCheck.result)}>
                                {reviewDetailCheck.result.replace(/_/g, " ")}
                              </IntentBadge>
                              <IntentBadge intent={reviewDetailCheck.consequence === "critical" ? "danger" : "neutral"}>
                                {reviewDetailCheck.consequence}
                              </IntentBadge>
                            </div>
                          </div>
                          <div className="mt-2 type-body">{reviewDetailCheck.reason}</div>
                          <div className="mt-1 truncate type-subtext text-muted-foreground">
                            observations{" "}
                            {reviewDetailCheck.observationIds.length > 0
                              ? reviewDetailCheck.observationIds.join(", ")
                              : "none"}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <div className="type-subtext font-medium text-muted-foreground">Source evidence</div>
                      {reviewDetailEvidence.length > 0 ? (
                        reviewDetailEvidence.map((artifact) => (
                          <div key={artifact.id} className="rounded-md bg-muted/40 px-3 py-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <code className="type-subtext text-muted-foreground">{artifact.id}</code>
                              <IntentBadge intent="neutral">{artifact.type}</IntentBadge>
                            </div>
                            <div className="mt-1 truncate type-body font-medium">{artifact.title}</div>
                            <div className="mt-1 type-subtext text-muted-foreground">
                              {artifact.source} · {artifact.collectedAt} · {artifact.actor}
                            </div>
                            <div className="mt-1 truncate type-subtext text-muted-foreground" title={artifact.contentHash}>
                              {artifact.contentHash}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                          No linked source artifacts were supplied for this finding.
                        </div>
                      )}
                      {reviewDetailReceipt ? (
                        <div className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="type-subtext font-medium text-muted-foreground">Receipt context</div>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <IntentBadge intent={soc2StatusIntent(reviewDetailReceipt.status)}>
                              {reviewDetailReceipt.status}
                            </IntentBadge>
                            <code className="type-subtext text-muted-foreground">{reviewDetailReceipt.id}</code>
                          </div>
                          <div className="mt-1 truncate type-subtext text-muted-foreground" title={reviewDetailReceipt.canonicalHash}>
                            {reviewDetailReceipt.canonicalHash}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <label className="mt-3 grid gap-1 type-subtext text-muted-foreground">
                    <span>Reviewer rationale</span>
                    <textarea
                      className={`${CANDIDATE_FIELD} min-h-20 resize-y`}
                      value={reviewDecisionRationaleDraft}
                      onChange={(event) => setReviewDecisionRationaleDraft(event.currentTarget.value)}
                      aria-describedby={[
                        `soc2-review-condition-${reviewDetailItem.id}`,
                        `soc2-review-reason-${reviewDetailItem.id}`,
                        `soc2-review-action-${reviewDetailItem.id}`,
                      ].join(" ")}
                    />
                  </label>
                  <div id={`soc2-review-submit-context-${reviewDetailItem.id}`} className="mt-2 type-subtext text-muted-foreground">
                    Review actions apply to the displayed condition, source evidence, consequence, and receipt context.
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() =>
                        void recordQueueReviewDecision(
                          reviewDetailItem,
                          "request_evidence",
                          reviewDecisionRationaleDraft,
                        )
                      }
                      disabled={reviewingId === reviewDetailItem.runId || !reviewDecisionRationaleDraft.trim()}
                      aria-describedby={`soc2-review-submit-context-${reviewDetailItem.id}`}
                      aria-label={`Request additional evidence for ${reviewDetailItem.controlId}`}
                    >
                      <Icon name="upload" size="sm" />
                      Request evidence
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        void recordQueueReviewDecision(
                          reviewDetailItem,
                          "record_exception",
                          reviewDecisionRationaleDraft,
                        )
                      }
                      disabled={reviewingId === reviewDetailItem.runId || !reviewDecisionRationaleDraft.trim()}
                      aria-describedby={`soc2-review-submit-context-${reviewDetailItem.id}`}
                      aria-label={`Record exception for ${reviewDetailItem.controlId}`}
                    >
                      <Icon name="shield-alert" size="sm" />
                      Record exception
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        void recordQueueReviewDecision(
                          reviewDetailItem,
                          "investigate_remediate",
                          reviewDecisionRationaleDraft,
                        )
                      }
                      disabled={reviewingId === reviewDetailItem.runId || !reviewDecisionRationaleDraft.trim()}
                      aria-describedby={`soc2-review-submit-context-${reviewDetailItem.id}`}
                      aria-label={`Open investigation for ${reviewDetailItem.controlId}`}
                    >
                      <Icon name="wrench" size="sm" />
                      Investigate
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setReviewDetailItem(null);
                        setReviewDetailRun(null);
                        setReviewDecisionRationaleDraft("");
                      }}
                      aria-label={`Close review detail for ${reviewDetailItem.controlId}`}
                    >
                      Close
                    </Button>
                  </div>
                </section>
              ) : null}
              {reviewQueue || reviewDetailItem || remediations ? (
                <div
                  className="mt-3 rounded-md border border-border bg-background px-3 py-3"
                  aria-labelledby="soc2-review-case-management-title"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div id="soc2-review-case-management-title" className="type-body font-medium">
                        Review case management
                      </div>
                      <div className="type-subtext text-muted-foreground">
                        Prioritized FAIL and UNKNOWN work stays tied to owner, severity, age, control, audit period,
                        rationale, evidence, policy, receipt, and remediation status.
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent={reviewCaseManagementFilters.length >= 5 ? "success" : "neutral"}>
                        {reviewCaseManagementFilters.length}/5 filters visible
                      </IntentBadge>
                      <IntentBadge intent={reviewerDisagreementCount > 0 ? "warning" : "success"}>
                        {reviewerDisagreementCount} unresolved reviewer disagreement
                      </IntentBadge>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 lg:grid-cols-4">
                    {reviewCaseManagementSignals.map((signal) => (
                      <div key={signal.label} className="rounded-md bg-muted/40 px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="truncate type-subtext text-muted-foreground">{signal.label}</div>
                          <IntentBadge intent={signal.intent as Intent}>case</IntentBadge>
                        </div>
                        <div className={`${NUM} mt-1 truncate type-body font-medium`}>{signal.value}</div>
                        <div className="mt-1 line-clamp-3 type-subtext text-muted-foreground">{signal.detail}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 type-subtext text-muted-foreground">
                    Reviewer disagreement remains visible until a request-evidence or investigation case is resolved
                    by a new verification run and linked receipt.
                  </div>
                </div>
              ) : null}
              {remediations ? (
                <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="type-body font-medium">Remediation follow-up</div>
                      <div className="type-subtext text-muted-foreground">
                        {remediations.summary.requiresReverification} require re-verification
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent={remediations.summary.open > 0 ? "warning" : "success"}>
                        {remediations.summary.open} open
                      </IntentBadge>
                      <IntentBadge intent={remediations.summary.exceptionRecorded > 0 ? "warning" : "success"}>
                        {remediations.summary.exceptionRecorded} exceptions recorded
                      </IntentBadge>
                    </div>
                  </div>
                  {remediations.items.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {remediations.items.slice(0, 4).map((item) => (
                        <div key={item.id} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <IntentBadge
                                  intent={
                                    item.state === "exception_recorded" || item.state === "accepted"
                                      ? "success"
                                      : item.state === "verified_after_fix"
                                        ? "success"
                                      : item.state === "investigation_open"
                                        ? "danger"
                                        : "warning"
                                  }
                                >
                                  {item.state.replace(/_/g, " ")}
                                </IntentBadge>
                                <code className="type-subtext text-muted-foreground">{item.controlId}</code>
                              </div>
                              <div className="mt-1 truncate type-body font-medium">{item.controlTitle}</div>
                              <div className="mt-1 line-clamp-2 type-subtext text-muted-foreground" title={item.action}>
                                {item.action}
                              </div>
                            </div>
                            <div className="text-right type-subtext text-muted-foreground">
                              <div>due {item.dueAt.slice(0, 10)}</div>
                              <div>{item.requiresReverification ? "re-verify" : "receipt linked"}</div>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 type-subtext text-muted-foreground">
                            <span>{item.owner}</span>
                            <span>assigned {item.assignment.assignee}</span>
                            <span>{item.comments.length} comments</span>
                            {item.escalation ? <span>{item.escalation.state.replace(/_/g, " ")} escalation</span> : null}
                            <span>{item.auditPeriodId}</span>
                            <span>{item.severity}</span>
                            <span>{item.evidenceRefs.length} evidence refs</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                      No SOC 2 remediation follow-up items.
                    </div>
                  )}
                </div>
              ) : null}
              {runs.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {runs.slice(0, 4).map((run) => (
                    <div key={run.id} className="rounded-md bg-muted/40 px-3 py-2">
                      <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
                        <span className="truncate type-body font-medium">{run.label}</span>
                        <div className="flex flex-wrap items-center gap-2">
                          <IntentBadge intent={run.summary.Exception > 0 ? "danger" : "success"}>
                            {run.summary.Exception} exceptions
                          </IntentBadge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void recordFirstReviewDecision(run)}
                            disabled={reviewingId === run.id}
                            aria-label={`Record review decision for saved run ${run.label}`}
                          >
                            <Icon name="check" size="sm" />
                            {reviewingId === run.id ? "Recording..." : "Review"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void recordFirstObservationCorrection(run)}
                            disabled={correctingId === run.id}
                            aria-label={`Record observation correction for saved run ${run.label}`}
                          >
                            <Icon name="pencil" size="sm" />
                            {correctingId === run.id ? "Correcting..." : "Correct"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void remapFirstControl(run)}
                            disabled={mappingId === run.id}
                            aria-label={`Remap first control for saved run ${run.label}`}
                          >
                            <Icon name="shuffle" size="sm" />
                            {mappingId === run.id ? "Mapping..." : "Remap"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void undoFirstControlMapping(run)}
                            disabled={mappingId === run.id}
                            aria-label={`Undo first control template mapping for saved run ${run.label}`}
                          >
                            <Icon name="undo" size="sm" />
                            Undo
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void loadScorecard(run)}
                            disabled={scorecardId === run.id || scorecardId === `latest:${run.id}`}
                            aria-label={`Open Phase 1 scorecard for saved run ${run.label}`}
                          >
                            <Icon name="activity" size="sm" />
                            {scorecardId === run.id || scorecardId === `latest:${run.id}` ? "Opening..." : "Scorecard"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void loadEvidenceInventory(run)}
                            disabled={inventoryId === run.id}
                            aria-label={`Open evidence inventory for saved run ${run.label}`}
                          >
                            <Icon name="archive" size="sm" />
                            {inventoryId === run.id ? "Opening..." : "Inventory"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void loadSourceAlerts(run)}
                            disabled={sourceAlertsId === run.id}
                            aria-label={`Open source freshness alerts for saved run ${run.label}`}
                          >
                            <Icon name="bell" size="sm" />
                            {sourceAlertsId === run.id ? "Opening..." : "Alerts"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void loadNotificationOutbox(run)}
                            disabled={notificationOutboxId === run.id}
                            aria-label={`Open notification outbox for saved run ${run.label}`}
                          >
                            <Icon name="send" size="sm" />
                            {notificationOutboxId === run.id ? "Opening..." : "Outbox"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void loadScheduleReport(run)}
                            disabled={scheduleReportId === run.id}
                            aria-label={`Open evidence refresh schedule for saved run ${run.label}`}
                          >
                            <Icon name="calendar-clock" size="sm" />
                            {scheduleReportId === run.id ? "Opening..." : "Schedule"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void replayRun(run)}
                            disabled={replayingId === run.id}
                            aria-label={`Replay saved run ${run.label}`}
                          >
                            <Icon name="rotate-ccw" size="sm" />
                            {replayingId === run.id ? "Replaying..." : "Replay"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void loadAuditPacket(run)}
                            disabled={auditPacketId === run.id}
                            aria-label={`Preview audit room readiness for saved run ${run.label}`}
                          >
                            <Icon name="file-text" size="sm" />
                            {auditPacketId === run.id ? "Opening..." : "Audit room"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void downloadExportPacket(run)}
                            disabled={exportingId === run.id}
                            aria-label={`Export audit-preparation packet for saved run ${run.label}`}
                          >
                            <Icon name="download" size="sm" />
                            {exportingId === run.id ? "Exporting..." : "Export"}
                          </Button>
                        </div>
                      </div>
                      <div className="mt-1 type-subtext text-muted-foreground">
                        {run.auditPeriodId} · {pct(run.summary.verifiedCoverage)} verified · {run.id}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-3 rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                  Save the representative package to create a durable preflight run.
                </div>
              )}
              {scorecard ? (
                <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="type-body font-medium">Visible Phase 1 scorecard</div>
                      <div className="type-subtext text-muted-foreground">
                        {scorecard.organizationName} · audit period {scorecard.auditPeriodId} · {scorecard.policyVersion}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent={scorecard.receiptReplaySuccess === 1 ? "success" : "warning"}>
                        replay {scorecard.receiptReplaySuccess == null ? "n/a" : pct(scorecard.receiptReplaySuccess)}
                      </IntentBadge>
                      <IntentBadge intent={scorecard.materialFalseAccepts === 0 ? "success" : "danger"}>
                        {scorecard.materialFalseAccepts} false accepts
                      </IntentBadge>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-2 xl:grid-cols-4">
                    {[
                      ["Coverage", pct(scorecard.verifiedControlCoverage)],
                      ["Unknown", pct(scorecard.unknownRate)],
                      ["Override", pct(scorecard.reviewerOverrideRate)],
                      [
                        "Median review",
                        scorecard.medianReviewTimeMinutes == null
                          ? "n/a"
                          : `${scorecard.medianReviewTimeMinutes.toFixed(1)} min`,
                      ],
                      [
                        "Population",
                        scorecard.populationReconciliationCoverage == null
                          ? "n/a"
                          : pct(scorecard.populationReconciliationCoverage),
                      ],
                      ["Silent approve", pct(scorecard.silentApproveRate)],
                      ["Findings", String(scorecard.totalFindings)],
                      ["First finding", `${scorecard.timeToFirstFindingMinutes} min`],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-card px-3 py-2">
                        <div className="type-subtext text-muted-foreground">{label}</div>
                        <div className={`${NUM} type-body font-medium`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  <div
                    className="mt-3 rounded-md border border-border bg-muted/30 px-3 py-3"
                    aria-labelledby="soc2-design-partner-proof-title"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div id="soc2-design-partner-proof-title" className="type-body font-medium">
                          Design-partner proof of value
                        </div>
                        <div className="type-subtext text-muted-foreground">
                          Baseline review time is compared with Excellent review time for the same audit period and
                          labeled findings.
                        </div>
                      </div>
                      <IntentBadge
                        intent={
                          scorecard.designPartnerProofOfValue.phaseGateStopSignals.length > 0
                            ? "warning"
                            : "success"
                        }
                      >
                        {scorecard.designPartnerProofOfValue.phaseGateStopSignals.length} stop signals
                      </IntentBadge>
                    </div>
                    <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-2 xl:grid-cols-4">
                      {[
                        [
                          "Baseline review",
                          `${scorecard.designPartnerProofOfValue.baselineReviewTimeMinutes.toFixed(0)} min`,
                        ],
                        [
                          "Excellent review",
                          scorecard.designPartnerProofOfValue.excellentReviewTimeMinutes == null
                            ? "n/a"
                            : `${scorecard.designPartnerProofOfValue.excellentReviewTimeMinutes.toFixed(1)} min`,
                        ],
                        [
                          "Time saved",
                          scorecard.designPartnerProofOfValue.reviewTimeSavedMinutes == null
                            ? "n/a"
                            : `${scorecard.designPartnerProofOfValue.reviewTimeSavedMinutes.toFixed(0)} min`,
                        ],
                        [
                          "Reduction",
                          scorecard.designPartnerProofOfValue.reviewTimeReductionRate == null
                            ? "n/a"
                            : pct(scorecard.designPartnerProofOfValue.reviewTimeReductionRate),
                        ],
                      ].map(([label, value]) => (
                        <div key={label} className="bg-card px-3 py-2">
                          <div className="type-subtext text-muted-foreground">{label}</div>
                          <div className={`${NUM} type-body font-medium`}>{value}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <IntentBadge intent="success">
                        {scorecard.designPartnerProofOfValue.acceptedFindingLabels} accepted findings
                      </IntentBadge>
                      <IntentBadge
                        intent={scorecard.designPartnerProofOfValue.rejectedFindingLabels > 0 ? "warning" : "success"}
                      >
                        {scorecard.designPartnerProofOfValue.rejectedFindingLabels} rejected findings
                      </IntentBadge>
                    </div>
                    <div className="mt-2 type-subtext text-muted-foreground">
                      {scorecard.designPartnerProofOfValue.comparisonBasis}
                    </div>
                  </div>
                  <div className="mt-3 overflow-x-auto rounded-md border border-border">
                    <div className="grid min-w-[42rem] grid-cols-[minmax(8rem,1fr)_repeat(4,minmax(4.5rem,0.65fr))] gap-px bg-border type-subtext">
                      {["Control family", "Coverage", "Unknown", "Overrides", "Population"].map((label) => (
                        <div key={label} className="bg-muted px-3 py-2 font-medium text-muted-foreground">
                          {label}
                        </div>
                      ))}
                      {scorecard.families.map((family) => (
                        <React.Fragment key={family.family}>
                          <div className="bg-card px-3 py-2">
                            <div className="font-medium">{family.family}</div>
                            <div className="text-muted-foreground">
                              {family.totalControls} controls · {family.totalFindings} findings
                            </div>
                          </div>
                          <div className={`${NUM} bg-card px-3 py-2`}>{pct(family.verifiedControlCoverage)}</div>
                          <div className={`${NUM} bg-card px-3 py-2`}>{pct(family.unknownRate)}</div>
                          <div className={`${NUM} bg-card px-3 py-2`}>{pct(family.reviewerOverrideRate)}</div>
                          <div className={`${NUM} bg-card px-3 py-2`}>
                            {family.populationReconciliationCoverage == null
                              ? "n/a"
                              : pct(family.populationReconciliationCoverage)}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 type-subtext text-muted-foreground">
                    {scorecard.boundaryNotice}
                  </div>
                </div>
              ) : null}
              {auditPacket ? (
                <div
                  className="mt-3 rounded-md border border-border bg-background px-3 py-3"
                  aria-labelledby="soc2-audit-room-readiness-title"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div id="soc2-audit-room-readiness-title" className="type-body font-medium">
                        Audit room readiness
                      </div>
                      <div className="truncate type-subtext text-muted-foreground">
                        Read-only preview for {auditPacket.organization.name} · {auditPacket.auditPeriod.id} · {auditPacket.format}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent="neutral">not audit opinion</IntentBadge>
                      <IntentBadge intent={auditPacket.exportPolicy.receiptsRedacted ? "warning" : "success"}>
                        {auditPacket.exportPolicy.receiptsRedacted ? "redacted" : "complete"}
                      </IntentBadge>
                      <IntentBadge intent={auditPacket.summary.totalExceptions > 0 ? "danger" : "success"}>
                        {auditPacket.summary.totalExceptions} exceptions
                      </IntentBadge>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-4">
                    {[
                      ["Controls", String(auditPacket.scopedControls.length)],
                      ["Findings", String(auditPacket.summary.totalFindings)],
                      ["Evidence refs", String(auditPacket.summary.scopedEvidenceRefs)],
                      ["Excluded", String(auditPacket.summary.excludedEvidenceRefs)],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-card px-3 py-2">
                        <div className="type-subtext text-muted-foreground">{label}</div>
                        <div className={`${NUM} type-body font-medium`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 rounded-md border border-border bg-muted/30 px-3 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="type-body font-medium">External reviewer packet controls</div>
                        <div className="type-subtext text-muted-foreground">
                          Read-only reviewer access, scoped packet contents, sample metadata, public receipt verification, and download log handoff.
                        </div>
                      </div>
                      <IntentBadge intent={auditPacket.exportPolicy.receiptsRedacted ? "warning" : "success"}>
                        {auditPacket.exportPolicy.receiptsRedacted ? "reviewer scoped" : "reviewer ready"}
                      </IntentBadge>
                    </div>
                    <div className="mt-3 grid gap-2 lg:grid-cols-5">
                      {auditRoomReadinessSignals.map((signal) => (
                        <div key={signal.label} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="truncate type-subtext text-muted-foreground">{signal.label}</div>
                            <IntentBadge intent={signal.intent as Intent}>gate</IntentBadge>
                          </div>
                          <div className={`${NUM} mt-1 truncate type-body font-medium`}>{signal.value}</div>
                          <div className="mt-1 line-clamp-3 type-subtext text-muted-foreground">{signal.detail}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 type-subtext text-muted-foreground">
                      This preview keeps audit-room collaboration bounded to scoped exports and explicit receipt replay metadata; it does not present readiness testing as a compliance certification.
                    </div>
                  </div>
                  <div className="mt-2 type-subtext text-muted-foreground">
                    Expired evidence {auditPacket.exportPolicy.includeExpiredEvidence ? "included" : "excluded"} ·{" "}
                    {auditPacket.exportPolicy.allowedEvidenceRefs
                      ? `${auditPacket.exportPolicy.allowedEvidenceRefs.length} permission-scoped refs`
                      : "all permitted evidence refs"}{" "}
                    · schema v{auditPacket.schemaVersion}
                  </div>
                  {auditPacket.exportPolicy.excludedEvidenceRefs > 0 ? (
                    <div className="mt-1 type-subtext text-muted-foreground">
                      {auditPacket.exportPolicy.excludedEvidenceRefs} evidence refs excluded by permission or retention policy before export.
                    </div>
                  ) : null}
                  {auditPacket.exceptions.length > 0 || auditPacket.findings.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {(auditPacket.exceptions.length > 0
                        ? auditPacket.exceptions
                        : auditPacket.findings
                      )
                        .slice(0, 3)
                        .map((finding) => (
                          <div
                            key={`${finding.controlId}:${finding.requirementId ?? "control"}:${finding.checkId ?? "control"}`}
                            className="rounded-md bg-muted/40 px-3 py-2"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <IntentBadge intent={soc2StatusIntent(finding.status)}>
                                {finding.status}
                              </IntentBadge>
                              <code className="type-subtext text-muted-foreground">{finding.controlId}</code>
                              <span className="type-subtext text-muted-foreground">
                                {finding.severity}
                              </span>
                            </div>
                            <div className="mt-1 line-clamp-2 type-body" title={finding.reason}>
                              {finding.reason}
                            </div>
                            <div className="mt-1 truncate type-subtext text-muted-foreground">
                              {finding.evidenceRefs.length > 0
                                ? finding.evidenceRefs.join(", ")
                                : "no evidence refs"}
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                      No failed or uncertain findings in this export packet.
                    </div>
                  )}
                  <div className="mt-3 grid gap-2 lg:grid-cols-2">
                    <div className="space-y-2">
                      <div className="type-subtext font-medium text-muted-foreground">Included evidence</div>
                      {auditPacket.evidenceMetadataRefs.slice(0, 3).map((row) => (
                        <div key={row.evidenceRef} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <code className="type-subtext text-muted-foreground">{row.evidenceRef}</code>
                            <IntentBadge intent={soc2RetentionIntent(row.retention.state)}>
                              {row.retention.state.replace(/_/g, " ")}
                            </IntentBadge>
                          </div>
                          <div className="mt-1 truncate type-body font-medium">{row.artifact.title}</div>
                          <div className="mt-1 truncate type-subtext text-muted-foreground">
                            {row.artifact.source} · {row.controlIds.join(", ")}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2">
                      <div className="type-subtext font-medium text-muted-foreground">Excluded evidence</div>
                      {auditPacket.excludedEvidenceMetadataRefs.length > 0 ? (
                        auditPacket.excludedEvidenceMetadataRefs.slice(0, 3).map((row) => (
                          <div key={row.evidenceRef} className="rounded-md bg-muted/40 px-3 py-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <code className="type-subtext text-muted-foreground">{row.evidenceRef}</code>
                              <IntentBadge intent="warning">
                                {row.exportDecision.reason.replace(/_/g, " ")}
                              </IntentBadge>
                            </div>
                            <div className="mt-1 truncate type-body font-medium">
                              {row.artifact?.title ?? "Artifact metadata unavailable"}
                            </div>
                            <div className="mt-1 truncate type-subtext text-muted-foreground">
                              {row.retention
                                ? `${row.retention.state.replace(/_/g, " ")} · retain until ${row.retention.retainUntil}`
                                : "retention metadata unavailable"}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                          No evidence refs were excluded by permissions or retention policy.
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 type-subtext text-muted-foreground">
                    {auditPacket.receipts.length} receipts · first canonical hash{" "}
                    <code>{auditPacket.receipts[0]?.canonicalHash.slice(0, 16) ?? "n/a"}</code>
                  </div>
                  <div className="mt-1 line-clamp-2 type-subtext text-muted-foreground" title={auditPacket.boundaryNotice}>
                    {auditPacket.boundaryNotice}
                  </div>
                </div>
              ) : null}
              {operationalEvidencePanelsOpen.length > 0 ? (
                <div
                  className="mt-3 rounded-md border border-border bg-background px-3 py-3"
                  aria-labelledby="soc2-operational-readiness-title"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div id="soc2-operational-readiness-title" className="type-body font-medium">
                        Operational evidence readiness
                      </div>
                      <div className="type-subtext text-muted-foreground">
                        Phase 2 signal from source health, refresh schedule, delivery outbox, and inventory lineage.
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent={operationalEvidenceBlockingSignals > 0 ? "warning" : "success"}>
                        {operationalEvidenceBlockingSignals} blocking signals
                      </IntentBadge>
                      <IntentBadge intent={operationalEvidencePanelsOpen.length >= 4 ? "success" : "neutral"}>
                        {operationalEvidencePanelsOpen.length}/4 panels opened
                      </IntentBadge>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-4">
                    {operationalEvidenceReadinessSignals.map(([label, value, intent]) => (
                      <div key={label} className="bg-card px-3 py-2">
                        <div className="type-subtext text-muted-foreground">{label}</div>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <span className={`${NUM} truncate type-body font-medium`}>{value}</span>
                          <IntentBadge intent={intent as Intent}>signal</IntentBadge>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 type-subtext text-muted-foreground">
                    Open Inventory, Alerts, Outbox, and Schedule on a saved run before relying on{" "}
                    {"continuous-control freshness"}.
                  </div>
                  <div
                    className="mt-3 rounded-md border border-border bg-muted/30 px-3 py-3"
                    aria-labelledby="soc2-continuous-loop-title"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div id="soc2-continuous-loop-title" className="type-body font-medium">
                          Continuous control loop
                        </div>
                        <div className="type-subtext text-muted-foreground">
                          Source change {"->"} owner notification {"->"} remediation recheck {"->"} stale-finding update.
                        </div>
                      </div>
                      <IntentBadge
                        intent={
                          sourceAlerts && notificationOutbox && remediations && scheduleReport
                            ? "success"
                            : "neutral"
                        }
                      >
                        {sourceAlerts && notificationOutbox && remediations && scheduleReport
                          ? "loop traceable"
                          : "open panels to trace"}
                      </IntentBadge>
                    </div>
                    <div className="mt-3 grid gap-2 lg:grid-cols-4">
                      {continuousControlLoopSignals.map((signal) => (
                        <div key={signal.label} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="truncate type-subtext text-muted-foreground">{signal.label}</div>
                            <IntentBadge intent={signal.intent as Intent}>state</IntentBadge>
                          </div>
                          <div className={`${NUM} mt-1 truncate type-body font-medium`}>{signal.value}</div>
                          <div className="mt-1 line-clamp-3 type-subtext text-muted-foreground">{signal.detail}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
              {sourceAlerts ? (
                <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="type-body font-medium">Source freshness alerts</div>
                      <div className="type-subtext text-muted-foreground">
                        {sourceAlerts.organization.name} · {sourceAlerts.auditPeriod.id}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent={sourceAlerts.summary.critical > 0 ? "danger" : "success"}>
                        {sourceAlerts.summary.critical} critical
                      </IntentBadge>
                      <IntentBadge intent={sourceAlerts.summary.high > 0 ? "warning" : "success"}>
                        {sourceAlerts.summary.high} high
                      </IntentBadge>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-4">
                    {[
                      ["Alerts", String(sourceAlerts.summary.totalAlerts)],
                      ["Controls at risk", String(sourceAlerts.summary.controlsAtRisk)],
                      ["Stale/blocked", String(sourceAlerts.summary.staleOrBlockedSources)],
                      ["Policy", sourceAlerts.policyVersion],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-card px-3 py-2">
                        <div className="type-subtext text-muted-foreground">{label}</div>
                        <div className={`${NUM} truncate type-body font-medium`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {sourceAlerts.alerts.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {sourceAlerts.alerts.slice(0, 4).map((alert) => (
                        <div key={alert.id} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <code className="type-subtext text-muted-foreground">{alert.evidenceRef}</code>
                              <div className="mt-1 type-body font-medium">{alert.reason}</div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <IntentBadge intent={alert.severity === "critical" ? "danger" : alert.severity === "high" ? "warning" : "neutral"}>
                                {alert.severity}
                              </IntentBadge>
                              <IntentBadge intent={soc2StatusIntent(alert.affectedStatus)}>
                                {alert.affectedStatus}
                              </IntentBadge>
                            </div>
                          </div>
                          <div className="mt-1 type-subtext text-muted-foreground">
                            {alert.source} · {alert.sourceHealth} · next check due {alert.nextCheckDueAt.slice(0, 10)}
                          </div>
                          <div className="mt-1 type-subtext text-muted-foreground">
                            {alert.controlIds.join(", ")} · {alert.findingIds.length} finding refs · {alert.observationIds.length} observations
                          </div>
                          <div className="mt-1 type-subtext text-muted-foreground">{alert.action}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                      No source freshness or health alerts for this run.
                    </div>
                  )}
                </div>
              ) : null}
              {scheduleReport ? (
                <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="type-body font-medium">Evidence refresh schedule</div>
                      <div className="type-subtext text-muted-foreground">
                        {scheduleReport.summary.affectedControls} controls degrade when refreshes are missed
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent={scheduleReport.summary.blocked > 0 ? "danger" : "success"}>
                        {scheduleReport.summary.blocked} blocked
                      </IntentBadge>
                      <IntentBadge intent={scheduleReport.summary.missed > 0 ? "warning" : "success"}>
                        {scheduleReport.summary.missed} missed
                      </IntentBadge>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-4">
                    {[
                      ["Controls", String(scheduleReport.summary.total)],
                      ["Due soon", String(scheduleReport.summary.dueSoon)],
                      ["On schedule", String(scheduleReport.summary.onSchedule)],
                      ["Affected", String(scheduleReport.summary.affectedControls)],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-card px-3 py-2">
                        <div className="type-subtext text-muted-foreground">{label}</div>
                        <div className={`${NUM} truncate type-body font-medium`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {scheduleReport.items.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {scheduleReport.items.slice(0, 4).map((item) => (
                        <div key={item.id} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="type-body font-medium">{item.controlId} · {item.frequency}</div>
                              <div className="mt-1 line-clamp-2 type-subtext text-muted-foreground">
                                {item.reason}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <IntentBadge intent={item.status === "blocked" ? "danger" : item.status === "missed" ? "warning" : "neutral"}>
                                {item.status.replace(/_/g, " ")}
                              </IntentBadge>
                              {item.affectedStatus ? (
                                <IntentBadge intent={soc2StatusIntent(item.affectedStatus)}>
                                  {item.affectedStatus}
                                </IntentBadge>
                              ) : null}
                            </div>
                          </div>
                          <div className="mt-2 grid gap-1 type-subtext text-muted-foreground sm:grid-cols-2">
                            <div className="truncate">Owner: {item.owner}</div>
                            <div className="truncate">
                              Latest: {item.latestEvidenceCollectedAt?.slice(0, 10) ?? "not collected"}
                            </div>
                            <div className="truncate">
                              Next due: {item.nextRefreshDueAt?.slice(0, 10) ?? "not scheduled"}
                            </div>
                            <div className="truncate">
                              Findings: <code>{item.findingIds.join(", ") || "n/a"}</code>
                            </div>
                          </div>
                          <div className="mt-1 type-subtext text-muted-foreground">{item.action}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                      No refresh schedules are tracked for this run.
                    </div>
                  )}
                </div>
              ) : null}
              {notificationOutbox ? (
                <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="type-body font-medium">Notification outbox</div>
                      <div className="type-subtext text-muted-foreground">
                        {notificationOutbox.settings.enabled ? "Delivery enabled" : "Delivery disabled"} ·{" "}
                        {notificationOutbox.settings.channels.join(", ")}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <IntentBadge intent={notificationOutbox.summary.pendingDeliveries > 0 ? "warning" : "neutral"}>
                        {notificationOutbox.summary.pendingDeliveries} pending
                      </IntentBadge>
                      <IntentBadge intent={notificationOutbox.summary.disabledDeliveries > 0 ? "neutral" : "success"}>
                        {notificationOutbox.summary.disabledDeliveries} disabled
                      </IntentBadge>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-4">
                    {[
                      ["Events", String(notificationOutbox.summary.total)],
                      ["Source alerts", String(notificationOutbox.summary.sourceAlerts)],
                      ["Remediation due", String(notificationOutbox.summary.remediationDue)],
                      ["Dedupe window", `${notificationOutbox.settings.dedupeWindowHours}h`],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-card px-3 py-2">
                        <div className="type-subtext text-muted-foreground">{label}</div>
                        <div className={`${NUM} truncate type-body font-medium`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {notificationOutbox.events.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {notificationOutbox.events.slice(0, 4).map((event) => (
                        <div key={event.id} className="rounded-md bg-muted/40 px-3 py-2">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="type-body font-medium">{event.title}</div>
                              <div className="mt-1 line-clamp-2 type-subtext text-muted-foreground">
                                {event.body}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <IntentBadge intent={event.kind === "source_alert" ? "warning" : "neutral"}>
                                {event.kind.replace(/_/g, " ")}
                              </IntentBadge>
                              <IntentBadge intent={event.severity === "critical" ? "danger" : event.severity === "high" ? "warning" : "neutral"}>
                                {event.severity}
                              </IntentBadge>
                            </div>
                          </div>
                          <div className="mt-2 grid gap-1 type-subtext text-muted-foreground sm:grid-cols-2">
                            <div className="truncate">
                              Controls: <code>{event.controlIds.join(", ") || "n/a"}</code>
                            </div>
                            <div className="truncate">
                              Evidence: <code>{event.evidenceRefs.join(", ") || "n/a"}</code>
                            </div>
                            <div className="truncate">
                              Findings: <code>{event.findingIds.join(", ") || "n/a"}</code>
                            </div>
                            <div className="truncate">Due {event.dueAt.slice(0, 10)}</div>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {event.deliveries.map((delivery) => (
                              <IntentBadge
                                key={delivery.dedupeKey}
                                intent={delivery.state === "pending" ? "warning" : delivery.state === "disabled" ? "neutral" : "danger"}
                              >
                                {delivery.channel}: {delivery.state.replace(/_/g, " ")}
                              </IntentBadge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-md border border-dashed border-border px-3 py-2 type-subtext text-muted-foreground">
                      No SOC 2 notifications are queued for this run.
                    </div>
                  )}
                </div>
              ) : null}
              {evidenceInventory ? (
                <div className="mt-3 rounded-md border border-border bg-background px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="type-body font-medium">Evidence inventory</div>
                      <div className="type-subtext text-muted-foreground">
                        {evidenceInventory.organization.name} · {evidenceInventory.auditPeriod.id}
                      </div>
                    </div>
                    <IntentBadge intent={evidenceInventory.summary.blockedSources > 0 ? "danger" : "success"}>
                      {evidenceInventory.summary.blockedSources} blocked
                    </IntentBadge>
                  </div>
                  <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-4">
                    {[
                      ["Artifacts", String(evidenceInventory.summary.totalArtifacts)],
                      ["Observations", String(evidenceInventory.summary.observationCount)],
                      ["Fresh", String(evidenceInventory.summary.freshArtifacts)],
                      ["Retained", String(evidenceInventory.summary.retainedArtifacts)],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-card px-3 py-2">
                        <div className="type-subtext text-muted-foreground">{label}</div>
                        <div className={`${NUM} type-body font-medium`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 space-y-2">
                    {evidenceInventory.artifacts.slice(0, 4).map((row) => (
                      <div key={row.artifact.id} className="rounded-md bg-muted/40 px-3 py-2">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <code className="type-subtext text-muted-foreground">{row.artifact.id}</code>
                            <div className="mt-1 truncate type-body font-medium">{row.artifact.title}</div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <IntentBadge intent={soc2InventoryHealthIntent(row.sourceHealth)}>
                              {row.sourceHealth}
                            </IntentBadge>
                            <IntentBadge intent={soc2RetentionIntent(row.retention.state)}>
                              {row.retention.state.replace(/_/g, " ")}
                            </IntentBadge>
                          </div>
                        </div>
                        <div className="mt-1 type-subtext text-muted-foreground">
                          {row.artifact.source} · {row.artifact.collectedAt} · retain until{" "}
                          {row.retention.retainUntil}
                        </div>
                        <div className="mt-1 type-subtext text-muted-foreground">
                          {row.controlIds.join(", ")} · {row.observationCount} observations ·{" "}
                          {row.findingIds.length} findings
                        </div>
                        <div
                          className="mt-1 truncate type-subtext text-muted-foreground"
                          title={row.provenance[0]?.value ?? row.artifact.contentHash}
                        >
                          {row.provenance[0]
                            ? `${row.provenance[0].type.replace(/_/g, " ")} · ${row.provenance[0].extractorVersion}`
                            : row.artifact.contentHash}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <Alert>
          <AlertTitle>Audit boundary</AlertTitle>
          <AlertDescription>{report.exportNotice}</AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}

type ActionTile = {
  label: string;
  detail: string;
  intent: Intent;
  icon: string;
  href?: string;
  onClick?: () => void;
};

function NextActionStrip({
  tasks,
  workflowCandidates,
  readiness,
  onOpen,
}: {
  tasks: EvidenceTaskSummaryDto[];
  workflowCandidates: EvidenceWorkflowCandidateDto[];
  readiness: EvidenceReadinessDto | undefined;
  onOpen: (id: number) => void;
}) {
  const reviewRun = tasks.find((task) => task.status === "review");
  const noPackRun = tasks.find((task) => task.status === "done" && !task.hasBundle);
  const admittedCandidate = workflowCandidates.find((candidate) => {
    return candidate.status === "admit";
  });
  const blockedReadiness = readiness?.items.find((item) => item.status === "blocked");
  const warningReadiness = readiness?.items.find((item) => item.status === "warning");

  const actions: ActionTile[] = [
    reviewRun
      ? {
          label: `Review run #${reviewRun.id}`,
          detail: reviewRun.title,
          intent: "warning",
          icon: "shield-alert",
          onClick: () => onOpen(reviewRun.id),
        }
      : {
          label: "Verification inbox clear",
          detail: "No workflow run is waiting on verifier action.",
          intent: "success",
          icon: "shield-check",
          href: "/inbox",
        },
    noPackRun
      ? {
          label: `Generate proof for #${noPackRun.id}`,
          detail: "Accepted run is missing a replayable evidence pack.",
          intent: "warning",
          icon: "archive",
          onClick: () => onOpen(noPackRun.id),
        }
      : {
          label: "Evidence packs aligned",
          detail: "Accepted runs have proof state or are ready for detail review.",
          intent: "success",
          icon: "archive",
          href: "/inbox",
        },
    blockedReadiness
      ? {
          label: blockedReadiness.label,
          detail: blockedReadiness.detail,
          intent: "danger",
          icon: "triangle-alert",
        }
      : warningReadiness
        ? {
            label: warningReadiness.label,
            detail: warningReadiness.detail,
            intent: "warning",
            icon: "activity",
          }
        : {
            label: "Verifier ready",
            detail: "Compiler, template readiness, evidence verify, and signing state are reporting.",
            intent: "success",
            icon: "badge-check",
          },
    admittedCandidate
      ? {
          label: `Draft ${admittedCandidate.name}`,
          detail: `${admittedCandidate.oracle} proves the observable consequence.`,
          intent: "neutral",
          icon: "workflow",
        }
      : {
          label: "Find a real workflow",
          detail: "Admit only candidates with an independent oracle.",
          intent: "warning",
          icon: "workflow",
        },
  ];

  return (
    <section className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
      {actions.map((action) => {
        const content = (
          <>
            <div className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2 type-body font-medium">
                <Icon name={action.icon} size="sm" />
                <span className="truncate">{action.label}</span>
              </span>
              <IntentBadge intent={action.intent}>
                {action.intent === "danger"
                  ? "blocked"
                  : action.intent === "warning"
                    ? "needs action"
                    : action.intent === "success"
                      ? "ready"
                      : "next"}
              </IntentBadge>
            </div>
            <p className="mt-2 line-clamp-2 type-subtext text-muted-foreground">
              {action.detail}
            </p>
          </>
        );
        const className =
          "min-h-[6.25rem] rounded-lg border border-border bg-card px-3 py-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/20";
        if (action.onClick) {
          return (
            <button key={action.label} type="button" onClick={action.onClick} className={className}>
              {content}
            </button>
          );
        }
        if (action.href) {
          return (
            <Link key={action.label} href={action.href} className={className}>
              {content}
            </Link>
          );
        }
        return (
          <div key={action.label} className={className}>
            {content}
          </div>
        );
      })}
    </section>
  );
}

function AgentConnectionGuide({
  workflowCandidates,
}: {
  workflowCandidates: EvidenceWorkflowCandidateDto[];
}) {
  const admitted = workflowCandidates.find((candidate) => candidate.status === "admit");
  const needsOracle = workflowCandidates.filter(
    (candidate) => candidate.status === "needs-oracle",
  ).length;
  const rejected = workflowCandidates.filter((candidate) => candidate.status === "reject").length;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Connect an agent</CardTitle>
            <CardDescription>
              Turn an external worker into a verifier run without leaving this console.
            </CardDescription>
          </div>
          <IntentBadge intent={admitted ? "success" : "warning"}>
            {admitted ? "candidate ready" : "needs oracle"}
          </IntentBadge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 md:grid-cols-4">
          {[
            ["1", "Save candidate", "Actor, verifier, oracle, consequence, evidence."],
            ["2", "Draft template", "Compile verifier checks from the admitted workflow."],
            ["3", "Create attempt", "Submit claims, receipts, decision, and evidence pack."],
            ["4", "Read verdict", "Store task id, route, bundle hash, and receipts."],
          ].map(([step, label, detail]) => (
            <div key={step} className="rounded-md border border-border bg-background px-3 py-2">
              <div className="flex items-center gap-2">
                <IntentBadge intent="neutral">{step}</IntentBadge>
                <div className="type-body font-medium">{label}</div>
              </div>
              <div className="mt-2 type-subtext text-muted-foreground">{detail}</div>
            </div>
          ))}
        </div>
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
          <div className="flex flex-wrap gap-2">
            <code className="rounded-md bg-muted px-2 py-1 type-subtext text-muted-foreground">
              POST /api/evidence/workflow-candidates
            </code>
            <code className="rounded-md bg-muted px-2 py-1 type-subtext text-muted-foreground">
              POST /api/evidence/workflow-candidates/template-draft
            </code>
            <code className="rounded-md bg-muted px-2 py-1 type-subtext text-muted-foreground">
              GET /api/evidence/tasks/:id/platform-verdict
            </code>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link href="#workflow-discovery">
              <Button type="button" variant="outline">
                <Icon name="workflow" size="sm" />
                Workflow discovery
              </Button>
            </Link>
            <Link href="/inbox">
              <Button type="button" variant="outline">
                <Icon name="shield-check" size="sm" />
                Decision queue
              </Button>
            </Link>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 type-subtext text-muted-foreground">
          <span>
            {admitted
              ? `${admitted.name} is ready for a canonical template draft.`
              : "No admitted candidate yet; add an independent truth source first."}
          </span>
          <IntentBadge intent={needsOracle > 0 ? "warning" : "neutral"}>
            {needsOracle} need oracle
          </IntentBadge>
          <IntentBadge intent={rejected > 0 ? "danger" : "neutral"}>
            {rejected} rejected
          </IntentBadge>
        </div>
      </CardContent>
    </Card>
  );
}

function ReadinessHealthCard({
  readiness,
}: {
  readiness: EvidenceReadinessDto | undefined;
}) {
  const receiptLatency = readiness?.receiptLatency;
  const recentFailures = readiness?.recentFailures ?? [];
  const compiler = readiness?.compiler;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Readiness health</CardTitle>
        <CardDescription>
          Compiler, signing, import latency, T0-T2 receipts, and recent failed checks.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-4">
          <div className="bg-card p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="type-subtext text-muted-foreground">Compiler probe</span>
              <IntentBadge
                intent={
                  compiler?.status === "ready"
                    ? "success"
                    : compiler?.status === "blocked"
                      ? "danger"
                      : "warning"
                }
              >
                {compiler?.status ?? "checking"}
              </IntentBadge>
            </div>
            <div className="mt-2 type-body font-medium">{compiler?.model ?? "checking"}</div>
            <div className="mt-1 type-subtext text-muted-foreground">
              {compiler?.detail ?? "reading compiler state"}
            </div>
          </div>
          <div className="bg-card p-3">
              <div className="type-subtext text-muted-foreground">Compiler timeout</div>
              <div className="mt-2 grid grid-cols-3 gap-2 type-body">
                <div>
                  <div className={`${NUM} font-medium`}>{formatMs(compiler?.timeoutMs)}</div>
                  <div className="type-subtext text-muted-foreground">limit</div>
                </div>
                <div>
                  <div className={`${NUM} font-medium`}>{compiler?.status ?? "checking"}</div>
                  <div className="type-subtext text-muted-foreground">status</div>
                </div>
                <div>
                  <div className={`${NUM} font-medium`}>{readiness?.items.length ?? 0}</div>
                  <div className="type-subtext text-muted-foreground">checks</div>
                </div>
              </div>
          </div>
          <div className="bg-card p-3">
            <div className="type-subtext text-muted-foreground">T0-T2 receipt latency</div>
            <div className="mt-2 grid grid-cols-3 gap-2 type-body">
              <div>
                <div className={`${NUM} font-medium`}>{formatMs(receiptLatency?.lastMs)}</div>
                <div className="type-subtext text-muted-foreground">last</div>
              </div>
              <div>
                <div className={`${NUM} font-medium`}>{formatMs(receiptLatency?.p95Ms)}</div>
                <div className="type-subtext text-muted-foreground">p95</div>
              </div>
              <div>
                <div className={`${NUM} font-medium`}>
                  {receiptLatency
                    ? `${receiptLatency.passing}/${receiptLatency.recentCount}`
                    : "0/0"}
                </div>
                <div className="type-subtext text-muted-foreground">pass</div>
              </div>
            </div>
          </div>
          <div className="bg-card p-3">
            <div className="type-subtext text-muted-foreground">Readiness checks</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(readiness?.items ?? []).map((item) => (
                <IntentBadge
                  key={item.id}
                  intent={
                    item.status === "ready"
                      ? "success"
                      : item.status === "blocked"
                        ? "danger"
                        : "warning"
                  }
                >
                  {item.label}
                </IntentBadge>
              ))}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
            Recent failed checks
          </div>
          {recentFailures.length > 0 ? (
            <div className="divide-y divide-border rounded-lg border border-border">
              {recentFailures.slice(0, 3).map((row) => (
                <div
                  key={`${row.command}-${row.createdAt}`}
                  className="grid gap-2 px-3 py-2 type-body sm:grid-cols-[auto_minmax(0,1fr)_auto]"
                >
                  <IntentBadge intent="danger">
                    {row.tier ?? "check"} exit {row.exitCode}
                  </IntentBadge>
                  <div className="min-w-0 truncate font-medium">{row.command}</div>
                  <div className={`${NUM} type-subtext text-muted-foreground`}>
                    {row.createdAt}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border px-3 py-3 type-body text-muted-foreground">
              No failed oracle or check receipts in the recent readiness window.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Soc2PreflightLaunchCard({ onOpen }: { onOpen: () => void }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>SOC 2 evidence preflight</CardTitle>
            <CardDescription>
              Open the control-matrix workspace when you are ready to import,
              review, receipt, and export a SOC 2 evidence package.
            </CardDescription>
          </div>
          <IntentBadge intent="neutral">on demand</IntentBadge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
          <div className="grid gap-2 sm:grid-cols-4">
            {[
              ["Import", "control matrix"],
              ["Verify", "policy checks"],
              ["Review", "exceptions"],
              ["Export", "receipts"],
            ].map(([label, detail]) => (
              <div key={label} className="rounded-md border border-border bg-background px-3 py-2">
                <div className="type-body font-medium">{label}</div>
                <div className="type-subtext text-muted-foreground">{detail}</div>
              </div>
            ))}
          </div>
          <Button type="button" onClick={onOpen}>
            <Icon name="folder-open" size="sm" />
            Open preflight
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TemplateLibraryCard({ templates }: { templates: WorkflowTemplateSummary[] }) {
  const [selected, setSelected] = React.useState(templates[0]?.id ?? "");
  const template = templates.find((item) => item.id === selected) ?? templates[0] ?? null;

  React.useEffect(() => {
    if (!templates.some((item) => item.id === selected)) {
      setSelected(templates[0]?.id ?? "");
    }
  }, [selected, templates]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Verifier templates</CardTitle>
        <CardDescription>
          Inspect authored verification requirements and the checks each template compiles.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {template ? (
          <>
            <div className="grid gap-2">
              {templates.map((action) => (
                <SelectableRow
                  key={action.id}
                  active={selected === action.id}
                  align="start"
                  onClick={() => setSelected(action.id)}
                  className="grid min-h-[3.75rem] w-full min-w-0 content-start gap-1"
                >
                  <span className="block min-w-0 break-words type-body font-medium [overflow-wrap:anywhere]">
                    {action.title}
                  </span>
                  <span
                    className={
                      selected === action.id
                        ? "block min-w-0 type-subtext text-foreground"
                        : "block min-w-0 type-subtext text-muted-foreground"
                    }
                  >
                    {action.expectedPredicates.length} compiled checks
                  </span>
                </SelectableRow>
              ))}
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
                    Template brief
                  </div>
                  <div className="mt-1 type-body font-medium">{template.requirement}</div>
                </div>
                <IntentBadge intent="success">endpoint ready</IntentBadge>
              </div>
              <p className="mt-2 line-clamp-3 type-body text-muted-foreground">{template.objective}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {template.expectedPredicates.map((check) => (
                  <IntentBadge key={check} intent="neutral">
                    {check}
                  </IntentBadge>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="rounded-md border border-dashed border-border px-3 py-3 type-body text-muted-foreground">
            No authored verifier templates are available yet. Templates appear here only after the live verifier endpoint returns them.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CandidateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-1 type-subtext text-muted-foreground">
      <span>{label}</span>
      <input
        className={CANDIDATE_FIELD}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </label>
  );
}

function candidateIntent(status: EvidenceWorkflowCandidateStatusDto): Intent {
  if (status === "admit") return "success";
  if (status === "reject") return "danger";
  return "warning";
}

function candidateStatusLabel(status: EvidenceWorkflowCandidateStatusDto): string {
  if (status === "admit") return "admitted";
  if (status === "needs-oracle") return "needs oracle";
  return "rejected";
}

function candidateStatusSummary(candidate: EvidenceWorkflowCandidateDto): string {
  if (candidate.status === "admit") {
    return "Ready to become a verifier template and ready for canonical Work intake because the work has an independent truth source, visible consequence, and evidence capture path.";
  }
  if (candidate.status === "reject") {
    return candidate.rejectionReason
      ? `Rejected: ${candidate.rejectionReason}`
      : "Rejected because the workflow does not yet produce verifier-grade evidence.";
  }
  return "Needs an independent oracle before it can become verifier-product work.";
}

function candidateBlockedReason(candidate: EvidenceWorkflowCandidateDto): string {
  if (candidate.status === "reject") {
    return candidate.rejectionReason || "Rejected candidates cannot create verifier runs.";
  }
  return "Add an independent truth source, observable consequence, and captureable evidence before drafting a verifier template.";
}

function templateBrief(candidate: EvidenceWorkflowCandidateDto): string {
  return [
    `Workflow: ${candidate.name}`,
    `Actors: actor=${candidate.actor}; verifier=${candidate.verifier}`,
    `External oracle: ${candidate.oracle}`,
    `Observable consequence: ${candidate.consequence}`,
    `Evidence capture: ${candidate.evidence}`,
  ].join("\n");
}

function CandidateSignal({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ComponentProps<typeof Icon>["name"];
}) {
  return (
    <div className="grid min-h-24 gap-2 rounded-md border border-border bg-background p-3">
      <div className="flex items-center gap-2 type-subtext font-medium uppercase tracking-normal text-muted-foreground">
        <Icon name={icon} size="sm" />
        <span>{label}</span>
      </div>
      <div className="type-body text-foreground">{value}</div>
    </div>
  );
}

function WorkflowTemplateDraftPreview({
  draft,
}: {
  draft: EvidenceWorkflowTemplateDraftResultDto;
}) {
  if (!draft.ok) return null;
  return (
    <div className="mt-3 space-y-3">
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <div className="rounded-md border border-border bg-muted/20 p-3">
          <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
            Authored template draft
          </div>
          <div className="mt-1 type-body font-medium text-foreground">{draft.draft.title}</div>
          <p className="mt-1 type-subtext text-muted-foreground">
            {draft.draft.objective}
          </p>
        </div>
        <div className="rounded-md border border-border bg-muted/20 p-3">
          <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
            Template shape
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <IntentBadge intent="neutral">{draft.draft.style}</IntentBadge>
            <IntentBadge intent="success">admitted candidate</IntentBadge>
          </div>
        </div>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <CandidateSignal
          label="Shipper role"
          value={draft.draft.actors.shipperRole}
          icon="users"
        />
        <CandidateSignal
          label="Verifier role"
          value={draft.draft.actors.verifierRole}
          icon="shield-check"
        />
      </div>
      <div className="rounded-md border border-border bg-muted/20 p-3">
        <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
          Requirement preview
        </div>
        <p className="mt-2 whitespace-pre-wrap type-subtext text-muted-foreground">
          {draft.draft.requirement}
        </p>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <div className="rounded-md border border-border bg-muted/20 p-3">
          <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
            Required checks
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {draft.draft.expectedPredicates.map((predicate) => (
              <IntentBadge key={predicate} intent="neutral">
                {predicate}
              </IntentBadge>
            ))}
          </div>
        </div>
        <div className="rounded-md border border-border bg-muted/20 p-3">
          <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
            External oracles
          </div>
          <div className="mt-2 space-y-1">
            {draft.draft.externalOracles.map((oracle) => (
              <div key={oracle} className="type-subtext text-muted-foreground">
                {oracle}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="rounded-md border border-border bg-muted/20 p-3">
        <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
          Draft requirements
        </div>
        <ul className="mt-2 grid gap-1 type-subtext text-muted-foreground">
          {draft.draft.requirements.map((requirement) => (
            <li key={requirement}>{requirement}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function WorkflowDiscoveryCard({
  candidates,
  onSaved,
}: {
  candidates: EvidenceWorkflowCandidateDto[];
  onSaved: () => void;
}) {
  const [draft, setDraft] = React.useState({
    name: "",
    actor: "",
    verifier: "",
    oracle: "",
    consequence: "",
    evidence: "",
    status: "needs-oracle" as EvidenceWorkflowCandidateStatusDto,
    rejectionReason: "",
  });
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [drafting, setDrafting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [templateDraft, setTemplateDraft] =
    React.useState<EvidenceWorkflowTemplateDraftResultDto | null>(null);
  const selected =
    candidates.find((candidate) => candidate.id === selectedId) ?? candidates[0] ?? null;

  React.useEffect(() => {
    if (candidates.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !candidates.some((candidate) => candidate.id === selectedId)) {
      setSelectedId(candidates[0]!.id);
    }
  }, [candidates, selectedId]);

  React.useEffect(() => {
    setTemplateDraft(null);
    setError(null);
  }, [selectedId]);

  function setField<K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function saveCandidate() {
    setSaving(true);
    setError(null);
    try {
      await apiCall(evidence.workflowCandidateSave, { body: draft });
      setDraft({
        name: "",
        actor: "",
        verifier: "",
        oracle: "",
        consequence: "",
        evidence: "",
        status: "needs-oracle",
        rejectionReason: "",
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function createTemplateDraft(candidateId: string) {
    setDrafting(true);
    setError(null);
    setTemplateDraft(null);
    try {
      const res = await apiCall(evidence.workflowTemplateDraft, {
        body: { candidateId },
      });
      setTemplateDraft(res);
      if (!res.ok) setError(res.reason);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDrafting(false);
    }
  }

  const admittedCount = candidates.filter((candidate) => candidate.status === "admit").length;
  const needsOracleCount = candidates.filter(
    (candidate) => candidate.status === "needs-oracle",
  ).length;
  const rejectedCount = candidates.filter((candidate) => candidate.status === "reject").length;

  return (
    <Card id="workflow-discovery">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Workflow discovery</CardTitle>
            <CardDescription>
              Admit only workflows with an independent oracle and observable consequence.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <IntentBadge intent={admittedCount > 0 ? "success" : "neutral"}>
              {admittedCount} admitted
            </IntentBadge>
            <IntentBadge intent={needsOracleCount > 0 ? "warning" : "neutral"}>
              {needsOracleCount} need oracle
            </IntentBadge>
            <IntentBadge intent={rejectedCount > 0 ? "danger" : "neutral"}>
              {rejectedCount} rejected
            </IntentBadge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 rounded-md border border-border bg-muted/20 p-3">
          <div className="grid gap-2 type-body sm:grid-cols-3">
            <div className="rounded-md bg-background p-3">
              <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
                Admission rule
              </div>
              <div className="mt-1 text-foreground">Independent truth source required</div>
            </div>
            <div className="rounded-md bg-background p-3">
              <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
                Consequence rule
              </div>
              <div className="mt-1 text-foreground">A real-world state must change</div>
            </div>
            <div className="rounded-md bg-background p-3">
              <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
                Evidence rule
              </div>
              <div className="mt-1 text-foreground">Capture must be replayable</div>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <CandidateInput
              label="Workflow"
              value={draft.name}
              onChange={(value) => setField("name", value)}
            />
            <CandidateInput
              label="Actor"
              value={draft.actor}
              onChange={(value) => setField("actor", value)}
            />
            <CandidateInput
              label="Verifier"
              value={draft.verifier}
              onChange={(value) => setField("verifier", value)}
            />
            <label className="grid gap-1 type-subtext text-muted-foreground">
              <span>Status</span>
              <select
                className={CANDIDATE_FIELD}
                value={draft.status}
                onChange={(event) =>
                  setField(
                    "status",
                    event.currentTarget.value as EvidenceWorkflowCandidateStatusDto,
                  )
                }
              >
                <option value="needs-oracle">needs oracle</option>
                <option value="admit">admit</option>
                <option value="reject">reject</option>
              </select>
            </label>
          </div>
          <CandidateInput
            label="Oracle"
            value={draft.oracle}
            onChange={(value) => setField("oracle", value)}
          />
          <CandidateInput
            label="Consequence"
            value={draft.consequence}
            onChange={(value) => setField("consequence", value)}
          />
          <CandidateInput
            label="Evidence"
            value={draft.evidence}
            onChange={(value) => setField("evidence", value)}
          />
          {draft.status === "reject" ? (
            <CandidateInput
              label="Rejection reason"
              value={draft.rejectionReason}
              onChange={(value) => setField("rejectionReason", value)}
            />
          ) : null}
          <div className="flex items-center justify-between gap-2">
            <Button
              onClick={() => void saveCandidate()}
              disabled={
                saving ||
                !draft.name.trim() ||
                !draft.actor.trim() ||
                !draft.verifier.trim() ||
                !draft.oracle.trim() ||
                !draft.consequence.trim() ||
                !draft.evidence.trim() ||
                (draft.status === "reject" && !draft.rejectionReason.trim())
              }
            >
              {saving ? "Saving..." : "Save candidate"}
            </Button>
            <code className="rounded-md bg-background px-2 py-1 type-subtext text-muted-foreground">
              POST /api/evidence/workflow-candidates
            </code>
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Candidate not saved</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>
        <div className="grid gap-3 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="divide-y divide-border rounded-lg border border-border">
            {candidates.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                onClick={() => setSelectedId(candidate.id)}
                className={[
                  "grid w-full gap-2 px-3 py-3 text-left transition-colors",
                  selected?.id === candidate.id
                    ? "bg-primary/8 text-foreground"
                    : "hover:bg-muted/40",
                ].join(" ")}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{candidate.name}</span>
                  <IntentBadge intent={candidateIntent(candidate.status)}>
                    {candidateStatusLabel(candidate.status)}
                  </IntentBadge>
                </div>
                <span className="truncate type-subtext text-muted-foreground">
                  {candidate.actor} {"->"} {candidate.verifier}
                </span>
                <span className="line-clamp-2 type-subtext text-muted-foreground">
                  {candidateStatusSummary(candidate)}
                </span>
              </button>
            ))}
          </div>
          {selected ? (
            <div className="rounded-lg border border-border bg-muted/15 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-medium">{selected.name}</div>
                  <div className="type-subtext text-muted-foreground">
                    Candidate detail · updated{" "}
                    {selected.updatedAt === "seed" ? "from seed" : selected.updatedAt}
                  </div>
                </div>
                <IntentBadge intent={candidateIntent(selected.status)}>
                  {candidateStatusLabel(selected.status)}
                </IntentBadge>
              </div>
              <div className="mt-3 rounded-md border border-border bg-background p-3 type-body">
                <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
                  Admission decision
                </div>
                <div className="mt-1 text-foreground">{candidateStatusSummary(selected)}</div>
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                <CandidateSignal label="Actor" value={selected.actor} icon="users" />
                <CandidateSignal label="Verifier" value={selected.verifier} icon="shield-check" />
                <CandidateSignal label="External truth source" value={selected.oracle} icon="search" />
                <CandidateSignal
                  label="Observable consequence"
                  value={selected.consequence}
                  icon="activity"
                />
                <CandidateSignal label="Evidence capture" value={selected.evidence} icon="archive" />
                <CandidateSignal
                  label="Admission requirement"
                  value={
                    selected.status === "admit"
                      ? "This candidate can create a draft for canonical verification work."
                      : candidateBlockedReason(selected)
                  }
                  icon={selected.status === "admit" ? "check" : "triangle-alert"}
                />
              </div>
              <div className="mt-3 rounded-md border border-border bg-background p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
                    Template brief
                  </span>
                  {selected.status === "admit" ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void createTemplateDraft(selected.id)}
                        disabled={drafting}
                      >
                        {drafting ? "Drafting..." : "Create draft"}
                      </Button>
                    </div>
                  ) : (
                    <span className="type-subtext text-muted-foreground">not ready</span>
                  )}
                </div>
                {templateDraft?.ok && templateDraft.draft.candidateId === selected.id ? (
                  <WorkflowTemplateDraftPreview draft={templateDraft} />
                ) : (
                  <pre className="mt-2 whitespace-pre-wrap type-subtext text-muted-foreground">
                    {templateBrief(selected)}
                  </pre>
                )}
              </div>
              {selected.status !== "admit" ? (
                <Alert className="mt-3">
                  <AlertTitle>Admission blocked</AlertTitle>
                  <AlertDescription>
                    {candidateBlockedReason(selected)}
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border p-4 type-body text-muted-foreground">
              Save a workflow candidate to create an admission detail.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function VerifierHome({
  tasks,
  agentImprovementEvidence,
  silent,
  templates = [],
  readiness,
  universalOverview,
  soc2Preflight,
  soc2ImportOnboardingState,
  soc2PolicyStudioState,
  soc2BatchJobs,
  soc2Runs,
  soc2PreflightOpen,
  workflowCandidates,
  listError,
  listLoading,
  onOpen,
  onOpenSoc2Preflight,
  onSoc2RunImported,
  onWorkflowCandidateSaved,
}: VerifierHomeProps) {
  const reviewCount = tasks.filter((t) => t.status === "review").length;
  const acceptedCount = tasks.filter((t) => decisionLabel(t) === "accepted").length;
  const packCount = tasks.filter((t) => t.hasBundle).length;
  const unsignedCopy =
    silent && !silent.ok
      ? `${silent.silent} silent approvals need review`
      : "override.human path measured";

  return (
    <div className="space-y-6">
      <PageHeader
        title="What is Excellent proving about agents and workflows?"
        description="See whether the coding agent is connected, what Excellent learned, whether behavior changed, which model route is supported, and which workflow evidence is still gated."
        tabs={[
          { href: "/", label: "Agent evidence" },
          { href: "/inbox", label: "Receipts" },
          { href: "/inbox?view=rejects", label: "Rejects" },
        ]}
      />

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(22rem,0.65fr)]">
        <div className="space-y-5">
          {agentImprovementEvidence}
          <ReadinessStrip silent={silent} tasks={tasks} readiness={readiness} />
          <D1VerificationLoopPanel
            tasks={tasks}
            workflowCandidates={workflowCandidates}
            readiness={readiness}
            universalOverview={universalOverview}
          />
          <VerificationQueueFocus tasks={tasks} onOpen={onOpen} />
          <NextActionStrip
            tasks={tasks}
            workflowCandidates={workflowCandidates}
            readiness={readiness}
            onOpen={onOpen}
          />
          <ReadinessHealthCard readiness={readiness} />
          {soc2PreflightOpen ? (
            <Soc2PreflightPanel
              report={soc2Preflight}
              importOnboardingState={soc2ImportOnboardingState}
              policyStudioState={soc2PolicyStudioState}
              batchJobs={soc2BatchJobs}
              runs={soc2Runs}
              onImported={onSoc2RunImported}
            />
          ) : (
            <Soc2PreflightLaunchCard onOpen={onOpenSoc2Preflight} />
          )}
          <div className="grid gap-4 sm:grid-cols-4">
            <StatCard
              title="Workflow runs"
              value={String(tasks.length)}
              description="reviewed verification work"
            />
            <StatCard
              title="Awaiting verifier"
              value={String(reviewCount)}
              leadingDot={reviewCount > 0 ? "warning" : "success"}
              description={reviewCount > 0 ? "verifier review inbox" : "Inbox clear"}
            />
            <StatCard
              title="Accepted with evidence"
              value={String(acceptedCount)}
              leadingDot={acceptedCount > 0 ? "success" : "neutral"}
              description="done runs with artifacts"
            />
            <StatCard
              title="Evidence packs"
              value={String(packCount)}
              leadingDot={packCount > 0 ? "success" : "warning"}
              description={unsignedCopy}
            />
          </div>

          <AgentConnectionGuide workflowCandidates={workflowCandidates} />

          <Card id="decision-queue">
            <CardHeader>
              <CardTitle>Decision queue</CardTitle>
              <CardDescription>
                Workflow claims, check receipts, actors, and evidence-pack state from the live verifier system.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {listLoading && tasks.length === 0 ? (
                <div className="grid gap-2">
                  <div className="h-9 animate-pulse rounded-md bg-muted" />
                  <div className="h-9 animate-pulse rounded-md bg-muted" />
                  <div className="h-9 animate-pulse rounded-md bg-muted" />
                </div>
              ) : listError ? (
                <Alert variant="destructive">
                  <AlertTitle>Verification data did not load</AlertTitle>
                  <AlertDescription>
                    {listError instanceof Error ? listError.message : String(listError)}
                  </AlertDescription>
                </Alert>
              ) : tasks.length === 0 ? (
                <div className="flex flex-col gap-3 rounded-lg border border-dashed border-border px-4 py-5">
                  <div className="flex items-center gap-2 type-body font-medium">
                    <Icon name="shield-check" size="sm" />
                    No workflow runs yet
                  </div>
                  <p className="max-w-2xl type-body text-muted-foreground">
                    Select an authored template or admit a real workflow through
                    the canonical Work through the public Work intake with an
                    external oracle. Runs appear here after they emit claims,
                    receipts, and a verifier decision.
                  </p>
                </div>
              ) : (
                <DataTable
                  rows={tasks}
                  columns={taskColumns(onOpen)}
                  rowKey={(r) => r.id}
                  wrapInCard={false}
                />
              )}
            </CardContent>
          </Card>
        </div>

        <aside className="space-y-5">
          <TemplateLibraryCard templates={templates} />
          <WorkflowDiscoveryCard
            candidates={workflowCandidates}
            onSaved={onWorkflowCandidateSaved}
          />
        </aside>
      </section>
    </div>
  );
}
